// All user-facing text. The only source file allowed to contain Cyrillic (CLAUDE.md).

export const ru = {
  app: {
    // The tab title: the product, then the organization (STAGE_7 7.7).
    titleSeparator: ' · ',
    // What the system runs on; no setting turns it off (the same words as
    // backend/seed/branding_texts.json, kept in step by hand).
    poweredBy: (product: string, version: string, vendor: string) =>
      vendor ? `Работает на ${product} · ${version} · ${vendor}` : `Работает на ${product} · ${version}`,
    poweredByUz: (product: string, version: string, vendor: string) =>
      vendor ? `${product} · ${version} · ${vendor} asosida ishlaydi` : `${product} · ${version} asosida ishlaydi`,
    loading: 'Загрузка…',
  },
  roles: {
    superadmin: 'Поставщик (техподдержка)',
    admin: 'Администратор',
    operator: 'Оператор',
    doctor: 'Медработник (врач/фельдшер)',
  },
  nav: {
    fleetMap: 'Машины на карте',
    menu: 'Разделы',
    dispatch: 'Диспетчерская',
    calls: 'Вызовы',
    journals: 'Журналы',
    patients: 'Пациенты',
    dictionaries: 'Справочники',
    shifts: 'Смены',
    incidents: 'Инциденты',
    complaints: 'Жалобы',
    notifications: 'Уведомления',
    audit: 'Аудит',
    vendor: 'Поставщик',
    reports: 'Отчёты',
    referrers: 'Направители',
    settings: 'Настройки',
  },
  header: {
    darkTheme: 'Тёмная тема',
    logout: 'Выйти',
    userMenu: 'Меню пользователя',
    changePassword: 'Сменить пароль',
    // An account with several roles works in one at a time (STAGE_5 5.0).
    mode: 'Режим',
    switchTo: (role: string) => `Режим: ${role}`,
    switchFailed: 'Не удалось сменить режим. Повторите попытку',
    sessions: 'Мои устройства',
  },
  // Where the account is signed in, one session per device (STAGE_5 5.0).
  sessions: {
    title: 'Мои устройства',
    hint: 'Здесь видно, где открыт ваш вход. «Завершить» закрывает сеанс на этом устройстве — например, на планшете, оставленном в машине.',
    thisDevice: 'Это устройство',
    unknownDevice: 'Неизвестное устройство',
    lastActive: (at: string) => `Последняя активность: ${at}`,
    mode: (role: string) => `Режим: ${role}`,
    end: 'Завершить',
    endAll: 'Выйти на всех устройствах',
    ended: 'Сеанс на устройстве завершён',
    failed: 'Не удалось завершить сеанс. Повторите попытку',
    empty: 'Открытых сеансов нет',
  },
  login: {
    title: 'Вход в систему',
    login: 'Логин',
    password: 'Пароль',
    submit: 'Войти',
    loginRequired: 'Введите логин',
    passwordRequired: 'Введите пароль',
    invalid: 'Неверный логин или пароль',
    tooMany: (seconds: number) =>
      `Слишком много попыток входа. Повторите через ${seconds} с`,
    network: 'Нет связи с сервером. Проверьте подключение и повторите',
    failed: 'Не удалось войти. Повторите попытку',
    mode: 'Режим работы',
    chooseMode: 'У вас несколько ролей — выберите, в каком режиме работать',
    modeRequired: 'Выберите режим',
    modeNotGranted: 'Этот режим вам не назначен',
  },
  session: {
    expired: 'Сеанс завершён. Войдите снова',
    restoreFailed: 'Нет связи с сервером. Повторите попытку',
  },
  config: {
    unavailable: 'Сервер недоступен',
    unavailableHint: 'Проверьте подключение к сети и повторите',
    retry: 'Повторить',
  },
  placeholder: {
    soon: (stage: number) => `Раздел появится на этапе ${stage}`,
  },
  // The crew app on the tablet (TZ 8, 15.3; STAGE_5).
  crew: {
    tabs: { calls: 'Вызовы', shift: 'Смена', expense: 'Расход', history: 'История' },
    online: 'Онлайн',
    offline: 'Офлайн',
    queued: 'Сохранено, отправится при появлении сети',
    queueTitle: 'Не отправлено',
    queueWaiting: (label: string) => `${label} — ждёт сети`,
    queueRejected: (label: string, reason: string) => `${label} — отклонено: ${reason}`,
    queueForget: 'Убрать',
    queueSent: (count: number) => `Отправлено действий: ${count}`,
    push: {
      enable: 'Включить уведомления о вызовах',
      on: 'Уведомления о вызовах включены',
      denied: 'Уведомления запрещены в настройках браузера',
      unsupported: 'Этот браузер не умеет уведомления',
    },
    waiting: (count: number) => {
      const mod10 = count % 10;
      const mod100 = count % 100;
      const word =
        mod10 === 1 && mod100 !== 11
          ? 'действие ждёт'
          : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)
            ? 'действия ждут'
            : 'действий ждут';
      return `${count} ${word}`;
    },
    offLine: 'Не на линии',
    onLine: 'на линии',
    waitingTitle: 'Ожидание вызова',
    waitingHint: 'Новое назначение придёт со звуком',
    offLineHint: 'Чтобы получать вызовы, выйдите на линию на вкладке «Смена»',
    toShift: 'Открыть «Смену»',
    soon: (task: string) => `Экран появится в задаче ${task}`,
    // "History" (TZ 8): the medic's calls of the shift or of seven days.
    history: {
      period: 'Период',
      shift: 'Смена',
      week: '7 дней',
      empty: 'Вызовов за этот период нет',
      noShift: 'Смен ещё не было',
      owedTitle: 'Не оплачены',
      pay: 'Оплата',
      owed: (amount: string) => `К оплате ${amount}`,
      totals: 'Итог',
      calls: 'Вызовов',
      amount: 'Сумма вызовов',
      received: 'Получено',
      nothingReceived: 'Оплат нет',
      stale: 'Нет связи — показан последний загруженный список',
      loadFailed: 'Список не загрузился, а сохранённого нет',
      back: '← К истории',
      call: 'Вызов',
      card: 'Карта вызова',
      cardView: 'Карта вызова (просмотр)',
      created: 'Принят',
      completed: 'Завершён',
      outcome: 'Результат выезда',
      payments: 'Платежи',
      noPayments: 'Платежей нет',
      noCard: 'Карта вызова не заполнялась',
    },
    device: 'Планшет',
    theme: 'Тема',
    themeDark: 'Тёмная',
    themeLight: 'Светлая',
    update: 'Доступна новая версия приложения',
    updateNow: 'Обновить',
    // The "Shift" tab (STAGE_5 5.2).
    shift: {
      goOnLineTitle: 'Выход на линию',
      vehicle: 'Машина',
      driver: 'Водитель',
      second: 'Второй медработник',
      secondNone: 'Без второго',
      driverChecked: 'Допуск водителя проверен (предрейсовый осмотр)',
      driverCheckedRequired: 'Без допуска водителя выйти на линию нельзя',
      goOnLine: 'Выйти на линию',
      goOnLineDone: 'Вы на линии',
      suggested: 'машина этого планшета',
      noVehicles: 'Свободных машин нет — все на линии или в ремонте',
      notLinked: 'Учётная запись не привязана к сотруднику. Обратитесь к администратору.',
      tabletUnbound: 'Планшет не привязан к машине — выберите машину сами. Чтобы привязать, передайте администратору идентификатор из раздела «Об устройстве».',
      tabletUnregistered: 'Планшета нет в справочнике устройств — выберите машину сами. Чтобы внести его, передайте администратору идентификатор из раздела «Об устройстве».',
      title: 'Смена',
      lead: 'Старший бригады',
      since: 'На линии с',
      calls: 'Вызовов',
      income: 'Приход',
      expenses: 'Расходы',
      cashExpenses: 'Расходы из выручки',
      reimbursable: 'Свои, к возмещению',
      noReceipt: 'Без чека (наличные и терминал)',
      toHandover: 'К сдаче',
      noIncome: 'Оплат пока нет',
      noExpenses: 'Расходов нет',
      slide: 'Проведите, чтобы снять с линии',
      slideLabel: 'Снять с линии',
      totalsTitle: 'Итог смены',
      totalsHint: 'Проверьте итог. После подтверждения он уйдёт заведующему, а планшет выйдет из вашей учётной записи.',
      confirm: 'Подтвердить итог',
      sentTitle: 'Итог отправлен заведующему',
      sentHint: (seconds: number) => `Выход из учётной записи через ${seconds} с`,
      signOutNow: 'Выйти сейчас',
      about: 'Об устройстве',
      deviceId: 'Идентификатор планшета',
      copy: 'Скопировать',
      copied: 'Скопировано',
      registered: (serial: string, model: string) => `В справочнике: ${model}, серийный ${serial}`,
      boundTo: (vehicle: string) => `Машина планшета: ${vehicle}`,
      notRegistered: 'Нет в справочнике устройств',
      notBound: 'Не привязан к машине',
      onLineFor: (vehicle: string, duration: string) => `Машина ${vehicle} · на линии ${duration}`,
    },
    // "Payment" and "Expense" on the tablet (TZ 8 screen 5, 11, 11.1; STAGE_5 5.5).
    payment: {
      title: (number: string) => `Оплата · ${number}`,
      owed: 'К оплате',
      currency: 'сум',
      breakdown: 'Разбивка',
      // The breakdown's groups, in the order the invoice has them.
      groups: {
        base: 'Базовая услуга',
        crew: 'Услуги врача',
        drugs: 'Препараты',
        other: 'Прочее',
      },
      total: 'Итого',
      included: 'входит в вызов',
      includedZero: 'входит в вызов · 0',
      times: (qty: string) => ` × ${qty}`,
      editServices: 'Изменить услуги и препараты',
      paidLocked: 'После оплаты расчёт меняет только администратор',
      method: 'Способ оплаты',
      amount: 'Сумма',
      receiptNo: 'Номер фискального чека',
      receiptNoHint: 'с терминала или кассы',
      photo: 'Фото чека',
      photoAgain: 'Переснять',
      contractOnly: 'Только для вызова от организации',
      accept: 'Принять оплату',
      accepted: 'Оплата принята',
      paidInFull: 'Оплачено полностью',
      done: 'К вызовам',
      already: (method: string, amount: string, receipt: string | null) =>
        receipt ? `${method} · ${amount} · ${receipt}` : `${method} · ${amount}`,
      receiptOf: (number: string) => `чек ${number}`,
      openFromCall: (amount: string) => `Оплата · ${amount} сум`,
    },
    expense: {
      title: 'Внести расход',
      category: 'Категория',
      amount: 'Сумма, сум',
      source: 'Источник средств',
      liters: 'Литры',
      odometer: 'Одометр, км',
      station: 'АЗС',
      photo: 'Фото чека',
      photoRequired: 'Фото чека обязательно',
      save: 'Сохранить расход',
      saved: 'Расход сохранён',
      ofShift: 'За смену',
      none: 'Расходов за смену нет',
      offLine: 'Расходы вносятся на линии',
      needCategory: 'Выберите категорию',
      needAmount: 'Укажите сумму',
    },
    photo: {
      take: 'Снять камерой',
      again: 'Переснять',
      taken: 'Фото добавлено',
      failed: 'Не удалось отправить фото',
    },
    // The call card by steps (TZ 6.3, 15.3; STAGE_5 5.4).
    report: {
      title: (number: string) => `Карта вызова · ${number}`,
      open: 'Карта вызова',
      openCount: (filled: number, total: number) => `Карта вызова ${filled}/${total}`,
      back: '← К вызову',
      callGone: 'Этого вызова нет среди вызовов бригады',
      close: 'Закрыть',
      readOnly: 'Только просмотр: оплаченный вызов меняет администратор',
      prev: 'Назад',
      skip: 'Пропустить',
      next: (step: string) => `Далее: ${step}`,
      done: 'Готово',
      saved: 'Сохранено',
      saveFailed: 'Не сохранилось — попробуйте ещё раз',
      stepOf: (step: number, total: number) => `${step} из ${total}`,
      missingHint: 'Откройте карту вызова',
      steps: {
        patient: 'Пациент',
        condition: 'Состояние',
        vitalsBefore: 'Показатели до',
        exam: 'Осмотр',
        diagnosis: 'Диагноз',
        help: 'Помощь',
        vitalsAfter: 'Показатели после',
        result: 'Результат',
      },
      patient: {
        name: 'ФИО',
        birthDate: 'Дата рождения',
        sex: 'Пол',
        fromCall: 'Из вызова. Исправленное попадёт и в вызов, и в карточку пациента; введённая дата рождения считается точной.',
        nameRequired: 'Введите ФИО',
        pinfl: 'ПИНФЛ',
        pinflHint: '14 цифр',
        pinflInvalid: 'ПИНФЛ — 14 цифр',
        document: 'Документ (серия, номер)',
        patientsCount: 'Пациентов на вызове',
      },
      condition: {
        general: 'Общее состояние',
        nature: 'Характер вызова',
        accidentCause: 'Причина несчастного случая',
        complaints: 'Жалобы',
        anamnesis: 'Анамнез',
        allergies: 'Аллергии',
        helpBefore: 'Помощь до прибытия',
        helpBeforeNone: 'Не оказана',
        helpBeforeWho: 'Кем оказана',
        onset: 'Начало заболевания',
        template: (text: string) => `+ ${text}`,
      },
      vitals: {
        bp: 'АД',
        bpWorking: 'Рабочее АД',
        systolic: 'систолическое',
        diastolic: 'диастолическое',
        pulse: 'Пульс',
        heartRate: 'ЧСС',
        respiratoryRate: 'ЧДД',
        temperature: 't°',
        spo2: 'SpO2',
        glucose: 'Глюкоза',
        norm: (range: string) => `норма ${range}`,
        outOfNorm: 'вне нормы',
      },
      exam: {
        hint: 'Всё по умолчанию «норма». Отметьте только отклонения.',
        sections: {
          skin: 'Кожные покровы',
          cardiovascular: 'Сердечно-сосудистая система',
          respiratory: 'Дыхание',
          abdomen: 'Живот и мочевыделение',
          neuro: 'Неврологический статус',
          glasgow: 'Шкала Глазго',
          newborn: 'Новорождённый',
        },
        fields: {
          skin: {
            colour: ['Цвет', 'обычной окраски', 'бледные', 'цианоз', 'желтушные', 'гиперемия', 'мраморные'],
            moisture: ['Влажность', 'нормальной влажности', 'влажные', 'сухие'],
            temperature: ['Температура кожи', 'тёплые', 'горячие', 'холодные'],
            edema: ['Отёки', 'нет', 'есть'],
            rash: ['Сыпь', 'нет', 'есть'],
            rash_location: ['Локализация сыпи'],
          },
          cardiovascular: {
            heart_sounds: ['Тоны сердца', 'ясные', 'приглушены', 'глухие', 'аритмичные'],
            pulse_character: ['Пульс', 'ритмичный', 'аритмичный', 'нитевидный', 'не определяется'],
            murmurs: ['Шумы', 'нет', 'систолический', 'диастолический'],
          },
          respiratory: {
            character: ['Дыхание', 'везикулярное', 'жёсткое', 'ослабленное', 'бронхиальное'],
            conduction: ['Проведение', 'во все отделы', 'ослаблено', 'не проводится'],
            throat: ['Зев', 'чистый', 'гиперемирован'],
            tonsils: ['Миндалины', 'не увеличены', 'увеличены', 'налёт'],
            lymph_nodes: ['Лимфоузлы', 'не увеличены', 'увеличены'],
          },
          abdomen: {
            tongue: ['Язык', 'влажный, чистый', 'сухой', 'обложен'],
            abdomen: ['Живот', 'мягкий, безболезненный', 'болезненный', 'напряжён', 'вздут'],
            peritoneal_signs: ['Перитонеальные симптомы', 'отрицательные', 'положительные'],
            urination: ['Мочеиспускание', 'свободное', 'болезненное', 'задержка'],
            diuresis: ['Диурез', 'в норме', 'снижен', 'повышен'],
            kidneys: ['Почки (Пастернацкий)', 'отрицательный', 'положительный'],
            menstruation: ['Менструация', 'не нарушена', 'нарушена', 'не применимо'],
          },
          neuro: {
            consciousness: ['Сознание', 'ясное', 'оглушение', 'сопор', 'кома'],
            pupils: ['Зрачки', 'D = S', 'анизокория', 'узкие', 'широкие'],
            meningeal_signs: ['Менингеальные знаки', 'отрицательные', 'положительные'],
            cranial_nerves: ['ЧМН', 'без патологии', 'патология'],
            sensitivity: ['Чувствительность', 'сохранена', 'снижена'],
            reflexes: ['Рефлексы', 'живые, D = S', 'снижены', 'повышены'],
            pathological_reflexes: ['Патологические рефлексы', 'нет', 'есть'],
            tone: ['Тонус', 'нормальный', 'снижен', 'повышен'],
            psyche: ['Психика', 'без особенностей', 'возбуждение', 'заторможенность'],
            behaviour: ['Поведение', 'адекватное', 'неадекватное'],
          },
          newborn: {
            umbilical_wound: ['Пупочная ранка', 'чистая', 'мокнет', 'гиперемия'],
            fontanelle: ['Родничок', 'на уровне костей', 'выбухает', 'западает'],
            feeding: ['Кормление', 'грудное', 'смешанное', 'искусственное'],
          },
        } as Record<string, Record<string, string[]>>,
        ecg: 'ЭКГ',
        localStatus: 'Локальный статус',
        apgar: 'Апгар',
        glasgowEyes: 'Открывание глаз',
        glasgowVerbal: 'Речевая реакция',
        glasgowMotor: 'Двигательная реакция',
        glasgowTotal: (total: string) => `Сумма: ${total}`,
      },
      diagnosis: {
        text: 'Диагноз',
        main: 'МКБ-10 основной',
        secondary: 'МКБ-10 сопутствующий',
        searchHint: 'Код (I21) или название; частые — сверху',
        extra: 'Дополнительно',
      },
      help: {
        services: 'Услуги',
        addService: '+ Добавить услугу',
        servicePick: 'Услуга',
        noServices: 'Услуги не добавлены',
        included: 'входит в вызов',
        byHand: 'Записано вручную, без прайса — не начисляется',
        equipment: 'Оборудование',
        equipmentList: [
          'Тонометр',
          'Термометр',
          'Пульсоксиметр',
          'Глюкометр',
          'ЭКГ',
          'Дефибриллятор',
          'Кислород',
          'Небулайзер',
          'Аспиратор',
          'Шина',
        ],
        place: 'Где оказана',
        drugs: 'Медикаменты',
        addDrug: '+ Добавить препарат',
        drugPick: 'Препарат',
        series: 'Серия',
        qty: 'Кол-во',
        price: (price: string, unit: string) => `${price} сум / ${unit}`,
        remove: 'Убрать',
        noDrugs: 'Препараты не использовались',
      },
      result: {
        help: 'Результат помощи',
        visit: 'Результат выезда',
        hospital: 'Стационар',
        hospitalization: 'Результат госпитализации',
        hospitalizationResults: {
          accepted: 'Принят',
          refused_by_admission: 'Отказ приёмного',
          transferred: 'Переведён',
        },
        signature: 'Подпись пациента пальцем',
        signatureSave: 'Сохранить подпись',
        signatureClear: 'Стереть',
        signatureSaved: 'Подпись сохранена',
        signatureEmpty: 'Сначала распишитесь в поле',
        onPaper: 'Подписано на бумаге',
        transfer103: 'Бригада 103: номер и время',
        activeVisit: 'Активное посещение',
        activeVisitNeeded: 'Нужно',
        activeVisitDate: 'Дата посещения',
        recommendations: 'Рекомендации',
        pdConsent: 'Согласие на обработку персональных данных получено',
      },
    },
    // "Calls" (STAGE_5 5.3).
    calls: {
      next: {
        assigned: 'Выехал',
        en_route: 'На месте',
        transport: 'Поехали с пациентом',
        complete: 'Завершить',
      },
      done: {
        en_route: 'Выехали',
        on_scene: 'На месте',
        transporting: 'Везём пациента',
        completed: 'Вызов завершён',
      },
      call: 'Позвонить',
      navigator: 'Навигатор',
      reason: 'Повод',
      patient: 'Пациент',
      payer: 'Плательщик',
      note: 'Комментарий',
      destination: 'Куда',
      details: 'Подъезд, этаж, код',
      callerLabel: 'Звонил',
      waiting: 'Ожидание',
      waitingOn: (time: string) => `Ожидание ${time}`,
      waitingStarted: 'Ожидание начато',
      waitingEnded: 'Ожидание закончено',
      changeDestination: 'Сменить назначение',
      destinationHospital: 'Стационар',
      destinationAddress: 'Или адрес',
      destinationSave: 'Сохранить назначение',
      destinationChanged: 'Назначение изменено',
      destinationRequired: 'Выберите стационар или введите адрес',
      problem: 'Проблема',
      problemTitle: 'Что случилось?',
      problemNote: 'Коротко, одной строкой',
      problemSend: 'Сообщить оператору',
      problemSent: 'Оператор уведомлён',
      problemNoTypes: 'Список видов происшествий пуст — сообщите оператору по рации',
      problemPhoto: 'Фото',
      problemPhotos: (count: number, limit: number) => `Снимков: ${count} из ${limit}`,
      problemPhotosClear: 'Убрать',
      incidentOpen: (type: string) => `Инцидент: ${type} · оператор уведомлён`,
      incidentResolved: (type: string) => `Инцидент закрыт: ${type}`,
      incidentStops: 'Вызов остановлен — ждите решения оператора',
      then: 'Затем',
      newCall: 'Новый вызов',
      accept: 'Принял',
      accepted: 'Вызов принят',
      urgency: (label: string) => `Новый вызов · ${label}`,
    },
    duration: (hours: number, minutes: number) =>
      hours > 0 ? `${hours} ч ${minutes} мин` : `${minutes} мин`,
    offlineTitle: 'Нет связи с сервером',
    offlineHint: 'Приложение подключится само, когда появится сеть',
  },
  // A period of days as two fields on a phone (components/PeriodPicker).
  period: { from: 'С', to: 'По' },
  common: {
    yes: 'Да',
    no: 'Нет',
    add: 'Добавить',
    edit: 'Изменить',
    save: 'Сохранить',
    cancel: 'Отмена',
    close: 'Закрыть',
    undo: 'Отменить',
    search: 'Поиск',
    active: 'Активен',
    showInactive: 'Показать неактивные',
    actions: 'Действия',
    saved: 'Сохранено',
    loading: 'Загрузка...',
    notSet: '—',
    total: (count: number) => `Всего: ${count}`,
    current: 'Действует',
    closed: 'В истории',
    future: 'С будущей даты',
    create: 'Создать',
  },
  errors: {
    notFound: 'Страница не найдена',
    pageFailed: 'Ошибка на странице',
    reload: 'Перезагрузить',
    toHome: 'На главную',
    required: 'Обязательное поле',
    invalidValue: 'Недопустимое значение',
    invalidPhone: 'Номер телефона Узбекистана: +998 XX XXX-XX-XX',
    duplicate: 'Такое значение уже используется',
    conflict: 'Запись конфликтует с существующими данными',
    // A 409 we have no sentence for: showing the code beats pretending to explain.
    refused: (code: string) => `Действие отклонено сервером: ${code}`,
    maintenance: 'Идёт обслуживание: изменения временно недоступны',
    // The server's own words: this shows when the form has no field for what it
    // rejected, and the point is that somebody notices and adds one.
    serverField: (field: string, message: string) => `${field}: ${message}`,
    saveFailed: 'Не удалось сохранить. Повторите попытку',
    loadFailed: 'Не удалось загрузить данные',
    network: 'Нет связи с сервером',
    maxLength: (max: number) => `Не длиннее ${max} символов`,
    minLength: (min: number) => `Не короче ${min} символов`,
    min: (min: number) => `Не меньше ${min}`,
    greaterThan: (min: number) => `Больше ${min}`,
    max: (max: number) => `Не больше ${max}`,
    integer: 'Целое число',
    json: 'Некорректный JSON',
    // A 422, said for a person (api/errors.ts invalidMessage): the field's label and
    // what is wrong; the server's English stays in its log.
    validation: {
      line: (field: string, reason: string) => `${field}: ${reason.charAt(0).toLowerCase()}${reason.slice(1)}`,
      missing: 'Обязательное поле',
      tooFew: (min: number) => `Нужно не меньше ${String(min)}`,
      tooMany: (max: number) => `Не больше ${String(max)} строк`,
      lessThan: (max: number) => `Меньше ${String(max)}`,
      receiptRequired: (min: number) =>
        `Для наличных и терминала нужен номер чека не короче ${String(min)} символов`,
      byType: {
        int_parsing: 'Нужно целое число',
        int_type: 'Нужно целое число',
        int_from_float: 'Нужно целое число',
        float_parsing: 'Нужно число',
        decimal_parsing: 'Нужно число',
        bool_parsing: 'Нужно «да» или «нет»',
        enum: 'Значение не из списка',
        literal_error: 'Значение не из списка',
        string_pattern_mismatch: 'Неверный формат',
        date_parsing: 'Неверная дата',
        date_from_datetime_parsing: 'Неверная дата',
        datetime_parsing: 'Неверные дата и время',
        datetime_from_date_parsing: 'Неверные дата и время',
        extra_forbidden: 'Лишнее поле',
        contract_only: 'По договору оплачивается только вызов от организации',
      } as Record<string, string>,
      fields: {
        amount: 'Сумма',
        method: 'Способ оплаты',
        fiscal_receipt_no: 'Номер фискального чека',
        receipt_photo_ref: 'Фото чека',
        reference: 'Номер документа',
        note: 'Комментарий',
        reason: 'Причина',
        description: 'Описание',
        name: 'Название',
        name_ru: 'Название',
        full_name: 'ФИО',
        phone: 'Телефон',
        phones: 'Телефоны',
        patient_name: 'ФИО пациента',
        patient_phone: 'Телефон пациента',
        patient_birth_date: 'Дата рождения',
        address: 'Адрес',
        reason_id: 'Повод',
        cancel_reason_id: 'Причина отмены',
        crew_id: 'Бригада',
        vehicle_id: 'Машина',
        lead_id: 'Старший бригады',
        driver_id: 'Водитель',
        category_id: 'Категория',
        type_id: 'Тип',
        source: 'Источник средств',
        liters: 'Литры',
        odometer: 'Одометр',
        qty: 'Количество',
        unit: 'Единица',
        series: 'Серия',
        price: 'Цена',
        value: 'Значение',
        valid_from: 'Действует с',
        services: 'Услуги',
        procedures: 'Услуги',
        items: 'Медикаменты',
        service_id: 'Услуга',
        hospital_id: 'Стационар',
        icd10_main: 'МКБ-10 основной',
        icd10_secondary: 'МКБ-10 сопутствующий',
        patients_count: 'Число пациентов',
        diagnosis_text: 'Диагноз',
        dest_hospital_id: 'Стационар',
        dest_address: 'Адрес назначения',
        transport_tariff_id: 'Тариф',
        contract_id: 'Договор',
        referrer_id: 'Направитель',
        login: 'Логин',
        password: 'Пароль',
        new_password: 'Новый пароль',
      } as Record<string, string>,
    },
  },
  dict: {
    title: 'Справочники',
    overviewHint: 'Выберите справочник. Простые поля правятся прямо в таблице.',
    overviewLink: 'Все справочники',
    deactivated: (name: string) => `«${name}» отключён`,
    activated: (name: string) => `«${name}» снова активен`,
    saveHint: 'Сохранить (Enter)',
    cancelHint: 'Отмена (Esc)',
    showOnMap: 'Показать на карте',
    groups: {
      people: 'Персонал и транспорт',
      medical: 'Приём вызова и медицина',
      prices: 'Цены и расходы',
      partners: 'Контрагенты',
      other: 'Прочее',
    },
    names: {
      staff: 'Сотрудники',
      vehicles: 'Машины',
      devices: 'Устройства',
      incidentTypes: 'Типы инцидентов',
      hospitals: 'Стационары',
      cancelReasons: 'Причины отмены',
      services: 'Услуги',
      transportTariffs: 'Тарифы транспортировки',
      tariffRules: 'Тарифные правила',
      holidays: 'Праздники',
      expenseCategories: 'Категории расходов',
      callReasons: 'Поводы вызова',
      callPlaces: 'Места вызова',
      reportTemplates: 'Шаблоны фраз карты вызова',
      icd10: 'МКБ-10',
      organizations: 'Организации',
      contracts: 'Договоры',
      users: 'Пользователи',
    },
    traccar: {
      hint: 'ID в Traccar присваивается системой: укажите IMEI трекера, и машина зарегистрируется в Traccar сама. Госномер становится именем устройства, снятая с учёта машина — отключённым устройством.',
      unavailable: 'Traccar недоступен: машина сохранена, но трекер не зарегистрирован. Нажмите «Повторить регистрацию», когда связь появится.',
      rejected: (detail: string | null | undefined) =>
        detail
          ? `Traccar отклонил регистрацию: ${detail}. Повторная попытка не поможет — разберитесь в Traccar.`
          : 'Traccar отклонил регистрацию. Повторная попытка не поможет — разберитесь в Traccar.',
      registered: 'Трекер зарегистрирован в Traccar',
      retry: 'Повторить регистрацию',
    },
    fields: {
      name: 'Название',
      fullName: 'ФИО',
      role: 'Должность',
      phone: 'Телефон',
      telegram: 'Telegram',
      telegramLinked: 'Привязан',
      telegramLinkedAt: (at: string) => `Привязан ${at}`,
      telegramNotLinked: 'Не привязан',
      sideNumber: 'Борт. №',
      plate: 'Госномер',
      model: 'Модель',
      status: 'Состояние',
      traccarId: 'ID в Traccar',
      traccarIdHint: 'из Traccar',
      tracker: 'Трекер',
      imei: 'IMEI',
      odometerStart: 'Начальный пробег, км',
      insuranceUntil: 'Страховка до',
      inspectionUntil: 'Техосмотр до',
      serial: 'Серийный номер',
      // The crew app's id of the tablet, from its "About the device" (STAGE_5 5.2).
      tabletId: 'Идентификатор планшета',
      templateField: 'Поле карты',
      templateText: 'Фраза',
      tabletIdHint: 'Из приложения бригады: «Смена» → «Об устройстве»',
      vehicle: 'Машина',
      note: 'Примечание',
      address: 'Адрес',
      coordinates: 'Координаты',
      lat: 'Широта',
      lng: 'Долгота',
      admissionPhone: 'Телефон приёмного',
      nameRu: 'Название (рус)',
      nameUz: 'Название (узб)',
      category: 'Категория',
      price: 'Цена, сум',
      unit: 'Единица',
      sortOrder: 'Порядок',
      date: 'Дата',
      code: 'Код',
      requiresFuel: 'Литры и пробег',
      requiresReceipt: 'Фото чека',
      urgency: 'Срочность',
      frequent: 'Частый',
      inn: 'ИНН',
      contacts: 'Контакты',
      requisites: 'Реквизиты',
      organization: 'Организация',
      number: 'Номер',
      validFrom: 'Действует с',
      validTo: 'Действует по',
      discount: 'Скидка, %',
      limit: 'Лимит, сум',
      paymentTerms: 'Оплата',
    },
    staffRoles: {
      doctor: 'Врач',
      paramedic: 'Фельдшер',
      nurse: 'Медсестра',
      driver: 'Водитель',
      dispatcher: 'Диспетчер',
      head: 'Заведующий',
    },
    allRoles: 'Все должности',
    allTemplateFields: 'Все поля',
    vehicleStatus: { on_line: 'На линии', off_line: 'Не на линии', breakdown: 'Поломка' },
    // Where a ready phrase goes on the call card (STAGE_5 5.4).
    templateFields: {
      complaints: 'Жалобы',
      anamnesis: 'Анамнез',
      recommendations: 'Рекомендации',
    },
    templatesHint: 'Фразы врач добавляет в карту вызова одним касанием. Порядок — по полю «Порядок».',
    incidentFields: {
      stopsCall: 'Останавливает вызов',
      disablesVehicle: 'Снимает машину с линии',
    },
    deviceStatus: {
      in_service: 'В машине',
      reserve: 'Резерв',
      repair: 'В ремонте',
      retired: 'Списан',
    },
    serviceCategories: {
      doctor_visit: 'Выезд врача',
      procedure: 'Манипуляции',
      medication: 'Медикаменты',
      other: 'Прочее',
    },
    urgency: { emergency: 'Экстренная', urgent: 'Срочная', non_urgent: 'Неотложная' },
    paymentTerms: { prepaid: 'Предоплата', postpaid: 'Постоплата' },
    codeHint: 'Латиница: строчные буквы, цифры и _',
    icdSearch: 'Код (например, I21) или часть названия',
    icdHint: 'Без запроса показаны частые коды. Частые коды врач видит первыми.',
  },
  users: {
    hint: 'Роли в системе задаются здесь и не зависят от должности сотрудника. При нескольких ролях пользователь выбирает режим при входе и переключает его в шапке.',
    login: 'Логин',
    password: 'Пароль',
    systemRole: 'Роли в системе',
    staff: 'Сотрудник',
    noStaff: 'Без привязки',
    searchPlaceholder: 'Логин или ФИО сотрудника',
    resetPassword: 'Задать пароль',
    resetTitle: (login: string) => `Новый пароль для «${login}»`,
    resetDone: 'Пароль изменён, другие сеансы этого пользователя завершены',
    changeTitle: 'Смена пароля',
    currentPassword: 'Текущий пароль',
    newPassword: 'Новый пароль',
    repeatPassword: 'Повторите пароль',
    changed: 'Пароль изменён',
    mismatch: 'Пароли не совпадают',
    wrongCurrent: 'Текущий пароль неверен',
    selfDeactivation: 'Нельзя отключить свою учётную запись',
    selfRoleChange: 'Нельзя снять с себя роль администратора',
    vendorAccount:
      'Учётная запись поставщика (техподдержки): её роль, активность и пароль меняет только сам поставщик',
    endSessions: 'Завершить сеансы',
    endSessionsConfirm: (login: string) => `Завершить все сеансы «${login}» на всех устройствах?`,
    sessionsEnded: 'Сеансы завершены',
    impersonate: 'Войти как',
  },
  map: {
    noTiles: 'Карта без подложки: не задан адрес тайлов (TILE_URL)',
    pickHint: 'Чтобы указать точку, нажмите «Изменить» или «Добавить» и кликните по карте',
    pickActive: 'Кликните по карте, чтобы поставить точку стационара',
  },
  tariffs: {
    versioningHint:
      'Изменение цены создаёт новую версию с сегодняшнего дня, прежняя сохраняется в истории. Название и активность правятся без новой версии.',
    newVersion: (date: string) => `Создана новая версия цены, действует с ${date}`,
    history: 'История версий',
    preview: 'Предпросмотр расчёта',
    previewHint:
      'Расчёт по ценам, действующим на указанный момент. Ничего не сохраняется.',

    closedReadOnly: 'Это версия из истории, её нельзя изменить',
    newTariff: 'Новый тариф',
    empty: 'Тарифов пока нет',
    select: 'Выберите тариф слева или создайте новый',
    fields: {
      basePrice: 'Подача, сум',
      includedKm: 'Включено км',
      perKmCity: 'За км в городе, сум',
      perKmOutside: 'За км за городом, сум',
      minPrice: 'Минимальная стоимость, сум',
    },
  },
  pricing: {
    previewEmpty: 'Задайте условия — и здесь появятся строки расчёта',
    at: 'Момент расчёта',
    services: 'Услуги',
    tariff: 'Тариф транспортировки',
    noTariff: 'Без транспортировки',
    distanceCity: 'Км по городу',
    distanceOutside: 'Км за городом',
    waitingMinutes: 'Ожидание, мин',
    serviceMinutes: 'Обслуживание, мин',
    extraPatients: 'Доп. пациентов',
    discount: 'Скидка по договору, %',
    cancelledAfterDeparture: 'Отмена после выезда',
    falseCall: 'Ложный выезд',
    calculate: 'Рассчитать',
    line: 'Строка',
    qty: 'Кол-во',
    unitPrice: 'Цена',
    amount: 'Сумма, сум',
    total: 'Итого',
    // The tariff's own name is on the call-out fee as well, so a kilometre line says
    // which kilometres it is about (call_charges.note).
    crewService: 'Услуга врача (из карты вызова)',
    lineNotes: {
      base_service: 'базовая',
      outside_km: 'за городом, км',
      city_km: 'по городу сверх включённых, км',
    },
    kinds: {
      service: 'Услуга',
      medication: 'Препарат',
      transport: 'Транспортировка',
      outside_km: 'За городом',
      waiting: 'Ожидание',
      long_service: 'Долгое обслуживание',
      rule: 'Правило',
      contract_discount: 'Скидка по договору',
    },
  },
  rules: {
    value: 'Значение',
    coefficient: 'Коэффициент',
    amount: 'Сумма, сум',
    pricePerHour: 'Цена за час, сум',
    pricePerKm: 'Сумма за 1 км, сум',
    freeMinutes: 'Бесплатно, мин',
    roundMinutes: 'Округление, мин',
    includedMinutes: 'Включено, мин',
    notCreated: 'Правило ещё не заведено',
    nightInterval: (from: string, to: string) => `Ночной интервал: ${from}-${to}`,
    toSettings: 'Изменить в настройках',
    inactiveHint: 'Неактивное правило не участвует в расчёте',
    codes: {
      night_coef: 'Ночной коэффициент',
      weekend_coef: 'Выходные и праздники',
      waiting_hour: 'Ожидание на месте',
      false_call: 'Ложный выезд',
      cancel_after_departure: 'Отмена после выезда',
      extra_patient: 'Второй пациент',
      outside_km: 'Километр за городом',
      long_service_hour: 'Долгое обслуживание',
    },
    descriptions: {
      night_coef: 'Умножает стоимость услуг и транспортировки; ночной интервал — в настройках',
      weekend_coef: 'Умножает стоимость в выходные и праздничные дни',
      waiting_hour: 'Цена часа ожидания; первые минуты бесплатно, округление вверх',
      false_call: 'Фиксированная сумма за ложный или пустой выезд',
      cancel_after_departure: 'Фиксированная сумма при отмене после выезда бригады',
      extra_patient: 'Фиксированная доплата за второго пациента',
      outside_km:
        'Сумма за каждый километр за пределами города, для любого типа вызова. Если в тарифе транспортировки задана цена за км за городом, применяется тариф, а правило не дублируется',
      long_service_hour:
        'Сумма за каждый час обслуживания сверх включённых; считается от прибытия на место до завершения вызова',
    },
  },
  dispatch: {
    title: 'Диспетчерская',
    // Parts of one line on a card: "+998 90 ... · звонил(а): мать · машина 01".
    join: (...parts: string[]) => parts.join(' · '),
    brand: (org: string) => `${org} · Диспетчерская`,
    shift: (from: string) => `Смена с ${from}`,
    noShift: 'Смена не открыта',
    soundOn: 'Звук вкл',
    soundOff: 'Звук выкл',
    soundHint: 'Звук тревог',
    theme: 'Тема',
    themeHint: 'Тёмная / светлая тема',
    newCall: 'Новый вызов',
    sections: 'Разделы',
    more: 'Ещё',
    queue: 'Очередь',
    active: 'В работе',
    vehicles: 'Машины',
    onLineCount: (count: number) => `${count} на линии`,
    queueEmpty: 'Очередь пуста — все вызовы назначены',
    activeEmpty: 'Нет вызовов в работе',
    all: 'Все',
    // Short status names for chips, cards and the map legend.
    statuses: {
      new: 'Новый',
      queued: 'В очереди',
      assigned: 'Назначен',
      en_route: 'В пути',
      on_scene: 'На месте',
      transporting: 'С пациентом',
      completed: 'Завершён',
      closed: 'Закрыт',
      cancelled: 'Отменён',
    },
    ago: (duration: string) => `${duration} назад`,
    // The crew's "Accepted" on the tablet (STAGE_5 5.3).
    acceptedAt: (time: string) => `принят бригадой ${time}`,
    notAccepted: (minutes: number) => `не принят ${minutes} мин`,
    promisedIn: (duration: string) => `обещано через ${duration}`,
    overdueBy: (duration: string) => `просрочено ${duration}`,
    noPromise: (duration: string) => `ждёт ${duration}, время не обещано`,
    inStatus: (status: string, duration: string) => `${status} ${duration}`,
    caller: (who: string) => `звонил(а): ${who}`,
    onVehicle: (side: string, doctor: string) => `машина ${side} · ${doctor}`,
    incident: {
      bar: (type: string) => `Инцидент: ${type}`,
      open: 'Инцидент открыт',
      reassign: 'Переназначить',
      destination: 'Сменить назначение',
      cancel: 'Отменить',
      resume: 'Продолжить',
      resumed: 'Вызов продолжается',
      toQueue: 'В очередь',
      cancelReason: 'Причина отмены',
      destinationHospital: 'Стационар',
      destinationAddress: 'Или адрес',
      destinationRequired: 'Выберите стационар или введите адрес',
      destinationChanged: 'Назначение изменено',
      cancelled: (number: string) => `Вызов ${number} отменён`,
      evacuation: (number: string) => `Эвакуация: создан вызов ${number}`,
      report: 'Инцидент',
      reportTitle: 'Зарегистрировать инцидент',
      type: 'Что случилось',
      description: 'Описание',
      severity: 'Серьёзность',
      vehicle: 'Машина',
      send: 'Зарегистрировать',
      sent: 'Инцидент зарегистрирован',
    },
    crewStates: {
      busy: 'На вызове',
      free: 'Свободна',
      off: 'Не на линии',
      breakdown: 'Поломка',
    },
    lead: 'Старший',
    driver: 'Водитель',
    second: 'Второй',
    gps: (age: string, speed: number | null) =>
      speed === null ? `GPS ${age} назад` : `GPS ${age} назад · ${speed.toFixed(0)} км/ч`,
    trackerSilent: (minutes: number) => `Трекер не отвечает ${minutes} мин`,
    trackerNever: 'Трекер не выходил на связь',
    trackerUnlinked: 'Трекер не привязан',
    then: (number: string, address: string) => `затем: ${number} · ${address}`,
    goingTo: (address: string) => `→ ${address}`,
    // Assigned and not started: the crew's next call, with nothing in hand before it.
    goingToAssigned: (number: string, address: string) => `→ ${number} · ${address} (назначен)`,
    freeUnknown: 'освобождается — оценка недоступна (вызов идёт дольше обычного)',
    freeIn: (minutes: number) => `освободится ≈ через ${minutes} мин`,
    goOnLine: 'Вывести на линию',
    clinic: 'Клиника',
    tabs: { calls: 'Вызовы', map: 'Карта', vehicles: 'Машины' },
    offline: (time: string) => `Нет связи с сервером — данные на ${time}`,
    showOnMap: 'Показать на карте',
    noPoint: 'без точки на карте',
    // Assigning, reassigning, the crew queue (STAGE_4 4.3).
    assignKm: (side: string, km: number | null, approx = false) =>
      km === null
        ? side
        : `${side} · ${approx ? '≈' : ''}${km.toFixed(1).replace('.', ',')} км`,
    stalePosition: (minutes: number) => `по последней позиции ${minutes} мин назад`,
    assignFree: 'свободна · назначить',
    assignNext: (after: string | null, more: number) =>
      (after ? `следующей · после ${after}` : 'следующей') + (more > 0 ? ` (+${more} в очереди)` : ''),
    reassign: 'Переназначить',
    hideReassign: 'Скрыть',
    toGeneralQueue: 'В общую очередь',
    toGeneralQueueHint: 'снять с бригады',
    reasonLabel: 'Причина',
    reasonReassign: (side: string) => `Причина переназначения на машину ${side}`,
    reasonToQueue: 'Причина снятия с бригады',
    reasonRequired: 'Укажите причину',
    confirm: 'Подтвердить',
    assigned: (number: string, side: string) => `Вызов ${number} назначен на машину ${side}`,
    queuedNext: (number: string, side: string) => `Вызов ${number} — следующий для машины ${side}`,
    reassigned: (number: string, side: string) => `Вызов ${number} переназначен на машину ${side}`,
    backToQueue: (number: string) => `Вызов ${number} возвращён в общую очередь`,
    undo: 'Отменить',
    undone: 'Отменено',
    undoReassignReason: (reason: string) => `Отмена переназначения: ${reason}`,
    alreadyWorked: 'Выделенный вызов уже у этой бригады',
    // Alarms and sound (STAGE_4 4.5).
    alarms: {
      soundOffFor: (left: string) => `Звук выкл ${left}`,
      soundOffHint: 'Звук тревог выключен на 10 минут. Нажмите, чтобы включить сейчас',
      soundOnHint: 'Выключить звук тревог на 10 минут',
      soundDisabled: 'Звук тревог выключен в настройках',
      unlock: 'Нажмите, чтобы включить звук тревог',
      overdue: (number: string, address: string) =>
        `Очередь: обещанное время превышено — ${number}, ${address}`,
      more: (count: number) => `ещё ${count}`,
      assign: 'Назначить',
      movingOffLine: 'Едет без смены',
      // A vehicle alarm beeps once; "Seen" takes it off every desk for an hour.
      seen: 'Принято',
      seenHint: 'Убрать эту тревогу на 60 минут — на всех местах диспетчера',
    },
    // The intake panel (STAGE_4 4.4).
    intake: {
      title: 'Новый вызов',
      escHint: 'Esc — закрыть',
      addressLabel: 'Адрес — текст, точка — кликом по карте справа',
      pointNone: 'Точка не поставлена: кликните по карте справа',
      pointSet: (lat: string, lng: string) => `Точка на карте: ${lat}, ${lng}`,
      clearPoint: 'Убрать точку',
      repeatTitle: 'Повторный?',
      repeatRow: (number: string, date: string, address: string) =>
        `Вызов ${number}, ${date}, ${address}`,
      asRepeat: 'Повторный к нему',
      takeData: 'Взять данные',
      promise: 'Обещано через',
      promiseMinutes: (minutes: number) => `${minutes} мин`,
      promiseOwn: 'своё время…',
      promiseAt: 'к',
      ownTimePast: 'Время должно быть позже текущего',
      // An estimate in the past means a call running far past the average: say so.
      eta: (side: string, minutes: number) =>
        minutes > 0
          ? `Машина ${side} освободится ориентировочно через ${minutes} мин`
          : `Машина ${side} освобождается — оценка недоступна (вызов идёт дольше обычного)`,
      hint: (seconds: number) => `Норма приёма — ${seconds} с. Сохранить можно и без второй строки.`,
      save: 'Сохранить',
      saveAssign: 'Сохранить и назначить',
      toQueue: 'В очередь',
      saved: (number: string) => `Вызов ${number} сохранён — назначьте его из очереди`,
      queued: (number: string, time: string) => `Вызов ${number} в очереди, обещано к ${time}`,
    },
    // Keys (STAGE_4 4.3): the same in the Russian and the English layout.
    keys: {
      title: 'Горячие клавиши',
      newCall: 'новый вызов',
      assign: 'назначить выделенный вызов (или первый в очереди) на машину с этим бортовым номером',
      escape: 'закрыть окно, снять выделение',
      help: 'эта подсказка',
      crews: 'Машины на линии',
      crewKey: (side: string, key: string) => `${side} — клавиша ${key}`,
      crewNoKey: (side: string) =>
        `${side} — без клавиши: бортовой номер не одна цифра`,
      noCrews: 'Нет машин на линии',
    },
  },
  // The operator's desk shift (TZ 7.6, STAGE_4 4.6).
  operatorShift: {
    startTitle: 'Начните смену',
    startText: 'Вызовы, принятые с этого момента, будут записаны в вашу смену.',
    start: 'Начать смену',
    started: 'Смена начата',
    endTitle: 'Завершить смену',
    handover: 'Передача смены',
    handoverHint: 'Что нужно знать следующему оператору: вызовы на контроле, перезвоны, машины',
    endAndLogout: 'Завершить смену и выйти',
    logoutOnly: 'Выйти, не завершая смену',
    ended: 'Смена завершена',
  },
  // The call journals (TZ 7.3, STAGE_4 4.6).
  journals: {
    title: 'Журналы',
    tabs: { kt: 'Приём (КТ)', kv: 'Карты вызовов (КВ)' },
    period: 'Период',
    status: 'Статус',
    crew: 'Бригада',
    type: 'Тип',
    payment: 'Оплата',
    payer: 'Плательщик',
    complaint: 'Жалоба',
    complaintWith: 'С жалобой',
    complaintWithout: 'Без жалобы',
    referrer: 'Направитель',
    search: 'Номер, телефон или ФИО',
    columnsButton: 'Колонки',
    columnsHint: 'Какие колонки показывать; выбор сохраняется за вами',
    columnsAll: 'Все',
    excel: 'Excel',
    reset: 'Сбросить фильтры',
    total: (count: number) => `Всего: ${count}`,
    yes: 'Да',
    no: 'Нет',
    columns: {
      kt: {
        status: 'Статус',
        daily_no: 'Суточный №',
        source: 'Тип обращения',
        number: 'Номер',
        created_at: 'Создан',
        intake_seconds: 'Приём',
        assigned_at: 'Передан бригаде',
        caller: 'Вызвавший',
        patient: 'Пациент',
        age: 'Возраст',
        sex: 'Пол',
        address: 'Адрес',
        place: 'Место вызова',
        reason: 'Повод',
        operator: 'Оператор',
        referrer: 'Направитель',
      },
      kv: {
        filled: 'КВ заполнена',
        type: 'Тип',
        kt_number: 'Номер КТ',
        kv_number: 'Номер КВ',
        status: 'Статус вызова',
        kv_status: 'Статус КВ',
        created_at: 'Создан',
        intake_at: 'Принят',
        handed_at: 'Передан бригаде',
        accepted_at: 'Принят бригадой',
        arrived_at: 'Доезд',
        transport_started_at: 'Начало транспортировки',
        hospital_arrived_at: 'Прибытие в стационар',
        completed_at: 'Завершён',
        returned_at: 'Возврат',
        crew: 'Бригада',
        address: 'Адрес',
        reason: 'Повод',
        nature: 'Характер',
        patient: 'Пациент',
        diagnosis: 'Диагноз',
        icd_main: 'МКБ основной',
        icd_secondary: 'МКБ сопутствующий',
        drugs: 'Медикаменты',
        outcome_visit: 'Результат выезда',
        hospitalization: 'Госпитализация (стационар)',
        hospitalization_result: 'Итог госпитализации',
        lead: 'Врач/фельдшер',
        lead_role: 'Должность',
        crew_members: 'Состав бригады',
        caller: 'Вызвавший',
        place: 'Место вызова',
        outcome_help: 'Помощь',
        spent_minutes: 'Затрачено, мин',
        drive_minutes: 'Доезд, мин',
        approach_km: 'Км доезда',
        accept_minutes: 'Принятие, мин',
      },
    } as Record<'kt' | 'kv', Record<string, string>>,
    kvStatuses: {
      assigned: 'Назначена',
      en_route: 'В пути',
      on_scene: 'На месте',
      transporting: 'С пациентом',
      completed: 'Завершена',
      reassigned: 'Переназначена',
      unassigned: 'Снята с бригады',
      cancelled: 'Отменена',
    } as Record<string, string>,
    sexes: { male: 'Мужской', female: 'Женский' } as Record<string, string>,
    hospitalizationResults: {
      accepted: 'Принят',
      refused_by_admission: 'Отказ приёмного отделения',
      transferred: 'Переведён',
    } as Record<string, string>,
  },
  // The printed forms (STAGE_7 7.4): each opens as a PDF in a tab of its own.
  print: {
    button: 'Печать',
    callReport: 'Карта вызова',
    invoice: 'Счёт',
    act: 'Акт',
    referrerAct: 'Акт сверки',
    crewTotals: 'Печать итога смены',
    showPatient: 'Показать пациенту',
    failed: 'Не удалось подготовить документ',
  },
  // Referrers and their fees (services.referrals): the admin's section, the call's
  // referrer in the intake form and the call card. The operator sees names only.
  referrers: {
    title: 'Направители',
    add: 'Добавить направителя',
    edit: 'Изменить',
    search: 'Название, телефон, контакт',
    showInactive: 'Показать неактивных',
    empty: 'Направителей пока нет',
    back: 'Направители',
    inactive: 'Неактивен',
    deactivate: 'Деактивировать',
    activate: 'Сделать активным',
    saved: 'Сохранено',
    types: {
      dispatch: 'Диспетчерская служба',
      doctor: 'Врач',
      clinic: 'Клиника',
      company: 'Компания',
    } as Record<string, string>,
    payMethods: {
      cash: 'Наличные',
      card: 'Карта',
      transfer: 'Перечисление',
    } as Record<string, string>,
    fields: {
      type: 'Тип',
      name: 'Название или ФИО',
      phone: 'Телефон',
      contactPerson: 'Контактное лицо',
      payMethod: 'Способ выплаты',
      payDetails: 'Реквизиты',
      comment: 'Комментарий',
      terms: 'Условие сейчас',
      debt: 'Долг',
    },
    terms: {
      title: 'Условия гонорара',
      none: 'Условие не задано',
      change: 'Новое условие',
      kind: 'Вид',
      kinds: { percent: 'Процент', fixed: 'Фиксированная сумма' } as Record<string, string>,
      value: 'Значение',
      percentHint: 'Процент от итога вызова без препаратов',
      fixedHint: 'Сумма за любой оплаченный вызов, сум',
      validFrom: 'Действует с',
      validFromFirstHint: 'Первое условие может начинаться любой датой: вызовы с этой даты получат гонорар',
      validFromNextHint: 'Новое условие действует с сегодняшнего дня или позже; начисленное не меняется',
      validTo: 'по',
      current: 'действует',
      history: 'История условий',
      percent: (value: string) => `${value} %`,
      fixed: (value: string) => `${value} сум за вызов`,
    },
    balance: {
      accrued: 'Начислено',
      paid: 'Выплачено',
      debt: 'Долг',
      overpaid: 'Переплата',
    },
    accruals: {
      title: 'Начисления',
      empty: 'Начислений нет',
      date: 'Дата',
      call: 'Вызов',
      patient: 'Пациент',
      total: 'Итог вызова',
      medications: 'Препараты',
      base: 'База',
      fee: 'Гонорар',
      state: 'Выплата',
      reversal: 'Сторно',
      deleted: 'вызов удалён',
      states: {
        paid: 'Выплачен',
        partial: 'Частично',
        unpaid: 'Не выплачен',
        credit: 'В счёт выплат',
        offset: 'Сторнирован',
      } as Record<string, string>,
      reasons: {
        payment: 'отмена платежа',
        reprice: 'пересчёт вызова',
        referrer: 'смена направителя',
        deleted: 'вызов удалён',
        restored: 'вызов восстановлен',
      } as Record<string, string>,
    },
    payouts: {
      title: 'Выплаты',
      empty: 'Выплат нет',
      pay: 'Провести выплату',
      date: 'Дата',
      amount: 'Сумма, сум',
      method: 'Способ',
      document: 'Номер документа',
      comment: 'Комментарий',
      by: 'Провёл',
      mode: 'Сумма выплаты',
      modeAmount: 'Указанная сумма',
      modeWhole: 'Весь долг на дату',
      wholeOn: 'Долг на конец дня',
      debtNow: (amount: string) => `Долг сейчас: ${amount} сум`,
      save: 'Провести',
      done: 'Выплата проведена',
      cancel: 'Отменить',
      cancelTitle: 'Отмена выплаты',
      cancelReason: 'Причина отмены',
      cancelSave: 'Отменить выплату',
      cancelled: 'Отменена',
      exceeds: (debt: string) => `Выплата больше долга (${debt} сум): аванс не допускается`,
      nothingOwed: 'На эту дату долга нет',
    },
    call: {
      title: 'Направитель',
      field: 'Направитель',
      placeholder: 'Поиск по названию или телефону',
      none: 'Не указан',
      change: 'Изменить',
      set: 'Указать',
      remove: 'Убрать направителя',
      dialog: 'Направитель вызова',
      reason: 'Причина',
      reasonHint: 'Вызов завершён: причина обязательна и попадёт в аудит',
      termsMissing: 'нет условия на дату вызова',
      feeEmpty: 'Гонорар не начислен',
      saved: 'Направитель сохранён',
    },
  },
  // The reports screen (TZ 13, STAGE_7 7.1). A report's own title, columns and filter
  // names come with it from the server (backend/seed/report_labels.json).
  reports: {
    title: 'Отчёты',
    period: 'Период',
    presets: {
      today: 'Сегодня',
      yesterday: 'Вчера',
      week: 'Неделя',
      month: 'Месяц',
      lastMonth: 'Прошлый месяц',
    },
    excel: 'Excel',
    // The phone (STAGE_7, mobile): the list, then one report on the whole screen.
    back: 'Отчёты',
    params: 'Период и фильтры',
    done: 'Готово',
    filtersCount: (count: number) => `фильтров: ${String(count)}`,
    separator: ' · ',
    rangeDash: '–',
    none: 'Нет доступных отчётов',
    empty: 'Нет данных за период',
    any: 'Все',
    reconcile: {
      column: 'Сверка',
      button: 'Сверено',
      title: 'Сверка кассы',
      expected: (amount: string) => `К сдаче по системе: ${amount} сум`,
      discrepancy: 'Расхождение, сум',
      discrepancyHint: 'Пересчитано минус к сдаче; недостача со знаком минус',
      note: 'Комментарий',
      save: 'Отметить сверку',
      done: 'Касса отмечена как сверенная',
    },
  },
  fleet: {
    title: 'Машины на карте',
    empty: 'Нет активных машин',
    noPosition: 'Позиции нет',
    pickVehicle: 'Выберите машину на карте',
    showTrack: 'Показать трек за сегодня',
    updated: (at: string) => `Обновлено ${at}`,
    status: {
      online: 'На связи',
      stale: 'Данные устарели',
      offline: 'Трекер офлайн',
      unlinked: 'Трекер не привязан',
    },
    fields: {
      crew: 'Бригада',
      lead: 'Старший бригады',
      driver: 'Водитель',
      call: 'Вызов',
      noCall: 'Без вызова',
      noCrew: 'Не на линии',
      speed: 'Скорость',
      ignition: 'Зажигание',
      age: 'Давность',
      tracker: 'Трекер',
    },
    kmh: (value: number) => `${value.toFixed(0)} км/ч`,
    // A vehicle where only text fits: the side number first, the plate after it.
    vehicleLabel: (sideNumber: string, plate: string) => `${sideNumber} · ${plate}`,
    ignitionOn: 'Включено',
    ignitionOff: 'Выключено',
    age: (seconds: number) =>
      seconds < 90
        ? `${seconds.toFixed(0)} с назад`
        : seconds < 5400
          ? `${(seconds / 60).toFixed(0)} мин назад`
          : `${(seconds / 3600).toFixed(1)} ч назад`,
  },
  calls: {
    title: 'Вызовы',
    create: 'Новый вызов',
    card: (number: string) => `Вызов ${number}`,
    back: 'К списку',
    intakeTimer: 'Приём вызова',
    save: 'Сохранить вызов',
    saved: 'Вызов принят',
    // The intake found an existing card by the number and the surname (STAGE_5).
    patientMatched: 'Вызов записан в карточку этого пациента',
    // Sex and age at intake (TZ 7.2): an age for a child under a year in months or days.
    sexShort: { male: 'м', female: 'ж' },
    ageModes: { age: 'лет', date: 'дата рождения' },
    ageUnits: { years: 'лет', months: 'мес', days: 'дней' },
    ageShort: (value: number, unit: 'years' | 'months' | 'days') =>
      `${value} ${{ years: 'г', months: 'мес', days: 'дн' }[unit]}`,
    sections: {
      main: 'Основное',
      second: 'Вторая строка',
      destination: 'Назначение',
      kv: 'Карта вызова',
      events: 'Журнал вызова',
      charges: 'Расчёт',
      payments: 'Оплата',
      services: 'Услуги',
      patient: 'Пациент',
      track: 'Трек и пробег',
    },
    track: {
      city: 'По городу',
      outside: 'За городом',
      total: 'Всего',
      odometer: 'По одометру',
      km: (value: number) => `${value.toFixed(1)} км`,
      manual: 'Километры введены вручную',
      measured: 'Километры посчитаны по треку',
      unavailable: (interval: string) =>
        `Позиций за интервал ${interval} нет: километры не начислены. Проверьте трекер машины.`,
      interval: (from: string, to: string) => `${from} — ${to}`,
      noInterval: 'вызов ещё не начинался',
      wholeCall: 'Интервал: весь вызов, шаг «Транспортировка» не был нажат',
      points: (used: number, dropped: number) =>
        dropped > 0 ? `Точек: ${used}, отброшено: ${dropped}` : `Точек: ${used}`,
      legendCity: 'город',
      legendOutside: 'за городом',
    },
    fields: {
      number: 'Номер',
      status: 'Статус',
      type: 'Тип',
      urgency: 'Признак',
      source: 'Тип обращения',
      kind: 'Вид',
      patientPhone: 'Телефон',
      patientName: 'ФИО пациента',
      address: 'Адрес',
      addressDetails: 'Подъезд, этаж, код',
      point: 'Точка на карте',
      pointSet: (lat: string, lng: string) => `Точка на карте: ${lat}, ${lng}`,
      pointNone: 'Точка не поставлена: выберите адрес из подсказок или кликните по карте',
      reason: 'Повод',
      reasonExtra: 'Доп. повод',
      place: 'Место вызова',
      payer: 'Плательщик',
      contract: 'Договор',
      callerName: 'Вызвавший',
      callerPhone: 'Телефон вызвавшего',
      callerRelation: 'Кем приходится',
      patientSex: 'Пол',
      patientAge: 'Возраст',
      patientBirthDate: 'Дата рождения',
      operatorNote: 'Комментарий оператора',
      destHospital: 'Стационар',
      destAddress: 'Адрес назначения',
      tariff: 'Тариф транспортировки',
      crew: 'Бригада',
      promisedAt: 'Обещано к',
      callbackAt: 'Перезвонить в',
      total: 'Итого, сум',
      paymentStatus: 'Оплата',
      createdAt: 'Создан',
      cancelReason: 'Причина отмены',
      distanceCity: 'Км по городу',
      distanceOutside: 'Км за городом',
    },
    statuses: {
      new: 'Новый',
      queued: 'В очереди',
      assigned: 'Назначен',
      en_route: 'В пути',
      on_scene: 'На месте',
      transporting: 'С пациентом',
      completed: 'Завершён',
      closed: 'Закрыт',
      cancelled: 'Отменён',
    },
    types: { doctor_visit: 'Выезд врача', transport: 'Транспортировка' },
    sources: {
      phone: 'По телефону',
      clinic: 'От медработника',
      telegram: 'Telegram',
      repeat: 'Повторный звонок',
      corporate: 'Корпоративный клиент',
      '112': 'От 112',
    },
    kinds: {
      primary: 'Первичный',
      repeat: 'Повторный',
      active_visit: 'Активное посещение',
      // A second vehicle carrying on somebody else's call (TZ 9): free, the first is paid.
      evacuation: 'Эвакуация',
    },
    payerTypes: { patient: 'Пациент', organization: 'Организация' },
    paymentStatuses: { unpaid: 'Не оплачен', partial: 'Частично', paid: 'Оплачен' },
    actions: {
      queue: 'В очередь',
      assign: 'Назначить',
      reassign: 'Переназначить',
      cancel: 'Отменить',
      close: 'Закрыть',
      en_route: 'Выезд',
      on_scene: 'На месте',
      transporting: 'Транспортировка',
      completed: 'Завершить',
      recalculate: 'Пересчитать',
      pay: 'Принять оплату',
      report: 'Заполнить карту',
      allowUnpaid: 'Закрыть без оплаты',
      services: 'Услуги вызова',
      tariff: 'Указать тариф',
    },
    /** Buttons that move the call the way the crew would (admin only in this section). */
    stepLabels: {
      en_route: 'Выезд',
      on_scene: 'На месте',
      transporting: 'Транспортировка',
      completed: 'Завершить',
    } as Record<string, string>,
    recent: {
      title: 'Последние вызовы',
      hint: 'Введите телефон — покажем вызовы по нему за 3 месяца',
      empty: 'По этому телефону вызовов не было',
      asRepeat: 'Повторный к этому вызову',
      takePatient: 'Взять данные пациента',
    },
    filters: {
      search: 'Номер, телефон или ФИО',
      status: 'Статус',
      type: 'Тип',
      payer: 'Плательщик',
      from: 'С',
      to: 'По',
      reset: 'Сбросить',
    },
    empty: 'Вызовов нет',
    noKv: 'Бригада не назначена',
    tariffMissing: 'Для транспортировки не задан тариф — вызов нельзя завершить',
    closeConfirm: 'Закрыть вызов? После закрытия он доступен только для чтения.',
    noCharges: 'Расчёт пуст: добавьте услуги или пересчитайте',
    reassignReason: 'Причина переназначения',
    cancelNote: 'Комментарий',
    eventTypes: {
      status_changed: 'Смена статуса',
      crew_assigned: 'Назначена бригада',
      crew_reassigned: 'Переназначена бригада',
      crew_unassigned: 'Снят с бригады, возвращён в общую очередь',
      waiting_started: 'Начало ожидания',
      waiting_ended: 'Конец ожидания',
      destination_changed: 'Смена назначения',
      incident_reported: 'Инцидент',
      incident_resolved: 'Инцидент закрыт',
      patient_notified: 'Уведомление пациенту',
      charges_recalculated: 'Пересчёт стоимости',
      mileage_unavailable: 'Нет позиций для пробега',
      base_left: 'Выезд из клиники',
      base_returned: 'Возврат в клинику',
      hospital_arrived: 'Прибытие в стационар',
      interruption_cleared: 'Вызов продолжен после инцидента',
      reopened: 'Открыт заново (поставщик)',
      status_forced: 'Статус изменён принудительно (поставщик)',
      deleted: 'Вызов удалён (поставщик)',
      restored: 'Вызов восстановлен (поставщик)',
      referrer_changed: 'Изменён направитель',
      services_changed: 'Услуги изменены вручную',
      zero_total_confirmed: 'Завершён с пустым расчётом (подтвердила бригада)',
    },
    // The calculation with nothing to pay (services.calls.charges_empty).
    chargesEmpty: 'Расчёт пуст: в вызове нет платных услуг',
    zeroConfirm: 'В вызове нет платных услуг — продолжить?',
    zeroConfirmOk: 'Завершить',
  },
  notificationsLog: {
    title: 'Уведомления',
    empty: 'Отправок пока нет',
    when: 'Когда',
    channel: 'Канал',
    channels: { telegram: 'Telegram', sms: 'SMS', push: 'Push' } as Record<string, string>,
    event: 'Событие',
    recipient: 'Адресат',
    body: 'Текст',
    state: 'Статус',
    statuses: {
      pending: 'В очереди',
      sent: 'Отправлено',
      failed: 'Не отправлено',
      skipped: 'Не отправлялось',
    } as Record<string, string>,
    error: 'Причина',
    attempts: 'Попыток',
    call: 'Вызов',
    retry: 'Отправить снова',
    retried: 'Поставлено в очередь',
    onlyFailed: 'Только неотправленные',
  },
  // Signed in, but the role opens nothing in this version of the app: never a blank page.
  noAccess: {
    title: 'Нет доступных разделов',
    hint: (role: string) =>
      `Для режима «${role}» в этой версии интерфейса нет разделов. Обновите страницу или обратитесь к администратору.`,
  },
  // What the whole system says across the top of every screen (STAGE_6 6.7).
  banners: {
    maintenance: 'Идёт обслуживание. Система работает только на чтение: начатые вызовы дорабатываются, новые не принимаются.',
    impersonating: (name: string, vendor: string) =>
      `Вы вошли как ${name} (поставщик: ${vendor}). Все действия записываются в аудит от обоих.`,
    stopImpersonating: 'Вернуться к своей учётной записи',
  },
  // The vendor's section (STAGE_6 6.7); seen by the superadmin role alone.
  vendor: {
    title: 'Поставщик (техподдержка)',
    tabs: {
      diagnostics: 'Диагностика',
      service: 'Обслуживание и функции',
      deleted: 'Удалённые вызовы',
      recalculate: 'Пересчёт вызовов',
      dictionaries: 'Справочники',
      users: 'Пользователи',
      updates: 'Обновления',
      maps: 'Карты',
    },
    updates: {
      current: 'Установлена версия',
      available: 'Доступна версия',
      none: 'Новее нет',
      unknown: 'Неизвестно: мониторинг выключен или ещё не отправлял отчёт',
      checkedAt: 'Проверено',
      notes: 'Что нового',
      how: 'Как обновить',
      howText:
        'Обновление выполняется на сервере: распакуйте архив новой версии и запустите sudo ./update.sh. Перед обновлением делается резервная копия; если система не поднимется, update.sh вернёт прежнюю версию сам (docs/install.md).',
      monitoring: 'Мониторинг',
      monitoringOn: 'разрешён отделением',
      monitoringOff: 'не разрешён отделением (Настройки → Поставщик)',
      notConfigured: 'адрес мониторинга не задан в выпуске',
      lastSent: 'Последний отчёт',
      lastError: 'Последняя ошибка',
      changelog: 'Изменения по версиям',
      noChangelog: 'Список изменений не найден',
    },
    maps: {
      parts: 'Обновляется',
      partNames: { tiles: 'карта (тайлы)', geocoder: 'поиск адреса' } as Record<string, string>,
      off: 'Своя карта и поиск адреса не включены (COMPOSE_PROFILES): обновлять нечего.',
      osmDate: 'Данные OpenStreetMap от',
      last: 'Последнее обновление',
      states: { running: 'идёт', done: 'успешно', failed: 'не удалось' } as Record<string, string>,
      steps: {
        request: 'запрос',
        space: 'место на диске',
        download: 'скачивание выгрузки',
        tiles: 'сборка тайлов',
        geocoder: 'сборка поиска адреса',
        swap: 'замена данных',
        restart: 'перезапуск',
        done: 'готово',
      } as Record<string, string>,
      step: 'Шаг',
      error: 'Причина',
      started: 'Начато',
      finished: 'Закончено',
      pending: 'Запрос ждёт исполнителя на сервере',
      requested: (by: string, at: string) => `Запрошено: ${by === 'schedule' ? 'по расписанию' : by}, ${at}`,
      now: 'Обновить сейчас',
      nowConfirm: 'Скачать свежие данные OpenStreetMap и пересобрать карту и поиск адреса? Это займёт до часа; пока идёт сборка, работают прежние карты.',
      nowStarted: 'Обновление запрошено',
      schedule: 'Расписание (cron, местное время)',
      scheduleHint: 'Например, 0 3 1 * * — первого числа в 03:00; 0 3 1 */3 * — раз в квартал',
      region: 'Откуда данные',
      pbfUrl: 'Выгрузка OSM (Geofabrik, .osm.pbf)',
      dumpUrl: 'Дамп Photon (.jsonl.zst)',
      countries: 'Страны для поиска адреса (коды через запятую)',
      busy: 'Обновление уже запрошено или идёт',
      disabled: 'Своя карта и поиск адреса не включены',
      reasons: {
        error: 'ошибка',
        timeout: 'превышено время прогона',
        stopped: 'сборку остановили извне',
        interrupted: 'исполнитель остановлен или не отвечает',
      } as Record<string, string>,
      reason: 'Что случилось',
      limit: (minutes: number) => `Лимит прогона: ${String(minutes)} мин`,
      running: (minutes: number) => `Идёт ${String(minutes)} мин`,
      progress: 'Ход сборки',
      phaseLine: (phase: string, elapsed: string) => `${phase} · ${elapsed}`,
      phases: {
        download: 'скачивание исходных данных',
        osm_pass1: 'чтение OSM, проход 1',
        osm_pass2: 'чтение OSM, проход 2',
        mbtiles: 'запись тайлов',
      } as Record<string, string>,
      bytes: (done: string, total: string | null) => (total ? `${done} из ≈ ${total}` : done),
      rate: (value: string) => `${value}/с`,
      units: ['Б', 'КБ', 'МБ', 'ГБ'] as const,
      logMoved: (at: string) => `Журнал обновлён ${at}`,
      stalled: 'Журнал не двигается больше 5 минут — проверьте journalctl -u sos-maps на сервере',
      timeout: 'Лимит одного прогона, часов',
      timeoutHint: 'Прогон дольше лимита останавливается, работают прежние карты. От 1 до 12 часов',
    },
    reason: 'Причина',
    reasonHint: 'Причина попадёт в аудит и в историю вызова',
    diagnostics: {
      refresh: 'Обновить',
      version: 'Версия',
      migration: 'Миграция базы',
      maintenance: 'Режим обслуживания',
      vendorAccess: 'Доступ поставщика',
      on: 'включён',
      off: 'выключен',
      workers: 'Фоновые процессы',
      worker: 'Процесс',
      lastRun: 'Последний запуск',
      secondsAgo: (value: number) => `${String(Math.round(value))} с назад`,
      lastError: 'Последняя ошибка',
      notifications: 'Очередь уведомлений',
      oldestPending: (value: number) => `Старейшее в очереди: ${String(Math.round(value))} с`,
      disk: 'Диск (файлы)',
      diskFree: (free: string, total: string, share: string) => `Свободно ${free} из ${total} (${share} %)`,
      traccar: 'Traccar',
      traccarOk: 'отвечает',
      traccarDown: (error: string) => `не отвечает: ${error}`,
      sessions: (sessions: number, users: number) =>
        `Живых сеансов: ${String(sessions)}, пользователей: ${String(users)}`,
      deletedCalls: 'Удалённых вызовов',
      unknown: 'нет данных',
      gigabytes: (value: string) => `${value} ГБ`,
    },
    service: {
      maintenance: 'Режим обслуживания',
      maintenanceHint:
        'Система только на чтение: начатые вызовы дорабатываются, новые не принимаются. Все видят баннер «Идёт обслуживание».',
      maintenanceOn: 'Режим обслуживания включён',
      maintenanceOff: 'Режим обслуживания выключен',
      flags: 'Функции (feature flags)',
      flagsHint: 'Имя латиницей: строчные буквы, цифры и подчёркивание.',
      flagName: 'Имя функции',
      addFlag: 'Добавить',
      noFlags: 'Функций нет',
      saved: 'Сохранено',
    },
    deleted: {
      empty: 'Удалённых вызовов нет',
      number: 'Вызов',
      deletedAt: 'Удалён',
      reason: 'Причина',
      restore: 'Восстановить',
      restored: 'Вызов восстановлен',
    },
    recalculate: {
      hint: 'После правки тарифа задним числом: вызовы пересчитываются по тарифам, действовавшим на момент приёма, включая закрытые.',
      numbers: 'Номера вызовов (id) через запятую или пробел',
      run: 'Пересчитать',
      result: 'Результат',
      oldTotal: 'Было',
      newTotal: 'Стало',
      invalid: 'Укажите номера (id) вызовов числами',
    },
    dictionaries: {
      hint: 'Справочники одним JSON-файлом: причины и места вызовов, причины отмены, стационары, типы инцидентов, категории расходов, праздники, услуги. При загрузке строки находятся по ключу (код, название, дата): совпавшие обновляются, новые добавляются, ничего не удаляется. Ошибка в одной строке — не пишется ничего.',
      export: 'Скачать JSON',
      import: 'Загрузить JSON',
      imported: 'Файл загружен',
      counts: (created: number, updated: number, unchanged: number) =>
        `добавлено ${String(created)}, обновлено ${String(updated)}, без изменений ${String(unchanged)}`,
      badFile: 'Это не файл справочников',
      rowError: (where: string, message: string) => `Ошибка в ${where}: ${message}`,
    },
    users: {
      hint: 'Вход под пользователем — для диагностики: 30 минут, без продления. Каждое действие попадает в аудит с обоими именами.',
      impersonate: 'Войти как',
      role: 'В роли',
    },
    call: {
      reopen: 'Открыть заново',
      forceStatus: 'Сменить статус принудительно',
      toStatus: 'Новый статус',
      delete: 'Удалить вызов',
      deleteHint: 'Вызов исчезнет из журналов, отчётов и диспетчерской. Восстановить можно в разделе «Поставщик».',
      edit: 'Править вызов',
      ticket: 'Контрольный талон',
      charges: 'Строки расчёта',
      services: 'Услуги',
      qty: 'Кол-во',
      addService: 'Добавить услугу',
      removeService: (n: number) => `Убрать услугу ${String(n)}`,
      kmHint: 'Километры вводятся вручную и помечаются в расчёте как ручные; пусто — как есть',
      nothingChanged: 'Ничего не изменено',
      cancelPayment: 'Отменить платёж',
      paymentCancelled: 'Платёж отменён',
      deleted: 'Вызов удалён',
      done: 'Готово',
    },
  },
  // The audit log (TZ 16; STAGE_6 6.7): filters and the Excel export.
  audit: {
    title: 'Аудит',
    period: 'Период',
    when: 'Когда',
    who: 'Кто',
    role: 'Роль',
    impersonatedBy: 'Вошёл как (поставщик)',
    entity: 'Объект',
    entityId: 'Номер',
    action: 'Действие',
    diff: 'Изменения',
    filters: {
      entity: 'Объект (таблица)',
      entityId: 'Номер объекта',
      userId: 'Пользователь',
      role: 'Роль',
      action: 'Действие',
      impersonated: 'Только «вошёл как»',
    },
    export: 'Excel',
    reset: 'Сбросить',
    empty: 'Записей нет',
  },
  // The complaint on a call and the patient's rating (TZ 7.7, 12; STAGE_6 6.5).
  complaints: {
    title: 'Жалобы',
    card: 'Жалоба и оценка',
    none: 'Жалоб нет',
    register: 'Зарегистрировать жалобу',
    edit: 'Изменить',
    close: 'Закрыть жалобу',
    text: 'Суть жалобы',
    assignee: 'Ответственный',
    noAssignee: 'Не назначен',
    status: 'Статус',
    statuses: {
      open: 'Открыта',
      in_progress: 'В работе',
      closed: 'Закрыта',
    } as Record<string, string>,
    result: 'Результат разбора',
    registeredBy: 'Зарегистрировал(а)',
    closedBy: 'Закрыл(а)',
    registered: 'Жалоба зарегистрирована, заведующий уведомлён',
    saved: 'Жалоба сохранена',
    closed: 'Жалоба закрыта',
    onlyAdminCloses: 'Закрывает жалобу администратор',
    rating: 'Оценка пациента',
    noRating: 'Пациент ещё не оценил выезд',
    openOnly: 'Только открытые',
    empty: 'Жалоб нет',
    call: 'Вызов',
    at: 'Когда',
    patient: 'Пациент',
    vehicle: 'Машина',
  },
  // The patient's page behind the link in the closing SMS: no account, the patient's
  // language (calls.notify_lang). Uzbek in Latin script, as the SMS texts are.
  rate: {
    ru: {
      title: 'Оцените выезд бригады',
      comment: 'Комментарий (необязательно)',
      send: 'Отправить',
      thanks: 'Спасибо! Ваша оценка поможет нам работать лучше.',
      done: 'Вы уже оценили этот выезд. Спасибо!',
      expired: 'Срок действия ссылки истёк.',
      notFound: 'Ссылка не найдена. Проверьте адрес из SMS.',
      failed: 'Не удалось отправить. Попробуйте ещё раз.',
      stars: ['Очень плохо', 'Плохо', 'Нормально', 'Хорошо', 'Отлично'],
    },
    uz: {
      title: "Brigada chaqiruvini baholang",
      comment: "Izoh (ixtiyoriy)",
      send: "Yuborish",
      thanks: "Rahmat! Bahoyingiz bizga yaxshiroq ishlashga yordam beradi.",
      done: "Siz bu chaqiruvni allaqachon baholagansiz. Rahmat!",
      expired: "Havolaning amal qilish muddati tugagan.",
      notFound: "Havola topilmadi. SMS dagi manzilni tekshiring.",
      failed: "Yuborib bo'lmadi. Qayta urinib ko'ring.",
      stars: ['Juda yomon', 'Yomon', "O'rtacha", 'Yaxshi', "A'lo"],
    },
  },
  incidents: {
    title: 'Инциденты',
    openOnly: 'Только открытые',
    empty: 'Инцидентов нет',
    number: 'Вызов',
    type: 'Тип',
    vehicle: 'Машина',
    reportedAt: 'Когда',
    reportedBy: 'Кто сообщил',
    description: 'Что случилось',
    photos: 'Фото',
    actions: 'Меры',
    actionsSave: 'Сохранить меры',
    actionsSaved: 'Меры сохранены',
    severity: 'Серьёзность',
    severities: {
      low: 'Низкая',
      medium: 'Средняя',
      high: 'Высокая',
    } as Record<string, string>,
    state: 'Состояние',
    open: 'Открыт',
    resolved: 'Закрыт',
    resolvedBy: 'Закрыл',
    resolve: 'Закрыть инцидент',
    resolved_: 'Инцидент закрыт',
    outcome: 'Итог',
    outcomes: {
      resolved: 'Решено',
      no_action_needed: 'Меры не нужны',
      escalated: 'Передано дальше',
      false_alarm: 'Ложная тревога',
    } as Record<string, string>,
    outcomeNote: 'Комментарий',
    stopsCall: 'Вызов остановлен',
    disablesVehicle: 'Машина снята с линии',
    vehicleBack: 'Машина вернётся в «Не на линии» после закрытия инцидента',
  },
  callErrors: {
    invalid_transition: 'Такой переход статуса невозможен',
    zero_total: 'В вызове нет платных услуг: завершение нужно подтвердить',
    // Referrers' payouts (services.referrals).
    payout_exceeds_debt: 'Выплата больше долга: аванс не допускается',
    nothing_owed: 'На эту дату долга нет',
    payout_cancelled: 'Выплата уже отменена',
    // The vendor's account and repairs (STAGE_6 6.7).
    vendor_account: 'Учётную запись поставщика меняет только сам поставщик',
    not_closed: 'Открыть заново можно только закрытый вызов',
    same_status: 'Вызов уже в этом статусе',
    call_deleted: 'Вызов уже удалён',
    call_not_deleted: 'Вызов не удалён',
    maintenance: 'Идёт обслуживание: изменения временно недоступны',
    dictionary_import: 'Файл справочников не принят',
    // The complaint on a call (STAGE_6 6.5).
    complaint_exists: 'По этому вызову жалоба уже зарегистрирована',
    complaint_closed: 'Жалоба уже закрыта',
    no_complaint: 'По этому вызову жалобы нет',
    // A crew takes queued calls, but works one at a time (STAGE_4 4.0).
    crew_busy: 'Бригада ещё работает на другом вызове — сначала завершите его',
    no_vehicle_alarm: 'Тревоги по этой машине уже нет',
    patient_inactive: 'Эта карточка уже объединена или отключена',
    not_in_crew_queue: 'Вызов не стоит в очереди бригады',
    crew_off_line: 'Бригада снята с линии',
    already_assigned: 'Бригада уже назначена, используйте переназначение',
    same_crew: 'Это та же бригада',
    no_active_kv: 'У вызова нет открытой карты',
    not_editable: 'Вызов уже в работе бригады, КТ не редактируется',
    report_incomplete: 'Карта вызова заполнена не полностью',
    call_closed: 'Вызов закрыт',
    call_completed: 'Вызов завершён: пересчёт только у администратора',
    call_unpaid: 'Вызов не оплачен',
    call_finished: 'Вызов завершён, события не принимаются',
    waiting_out_of_order: 'Ожидание уже открыто или ещё не начиналось',
    no_open_shift: 'Откройте смену оператора',
    not_a_transport_call: 'Транспортировка возможна только для перевозки',
    transport_tariff_required: 'Сначала укажите тариф транспортировки',
    exceeds_shift_cash: 'Больше наличной выручки смены',
    no_open_crew: 'Сначала выйдите на линию',
    already_reviewed: 'Расход уже проверен',
    totals_confirmed: 'Итог смены подтверждён администратором',
    vehicle_busy: 'Машина уже на линии',
    vehicle_not_off_line: 'Машина недоступна',
    staff_busy: 'Сотрудник уже в другой бригаде',
    crew_has_active_call: 'У бригады есть активный вызов',
    crew_already_ended: 'Бригада уже снята с линии',
    shift_already_open: 'Смена уже открыта',
    shift_already_ended: 'Смена уже закрыта',
  } as Record<string, string>,
  report: {
    title: 'Карта вызова',
    missing: (fields: string) => `Для завершения не хватает: ${fields}`,
    fieldNames: {
      general_condition: 'общее состояние',
      diagnosis_text: 'диагноз',
      outcome_help: 'результат помощи',
      outcome_visit: 'результат выезда',
      hospital_id: 'стационар',
      refusal_signature: 'подпись отказа',
      active_visit_date: 'дата активного посещения',
    } as Record<string, string>,
    generalCondition: 'Общее состояние',
    callNature: 'Характер вызова',
    patientsCount: 'Пациентов',
    diagnosis: 'Диагноз',
    complaints: 'Жалобы',
    anamnesis: 'Анамнез',
    bpBefore: 'АД до',
    pulseBefore: 'Пульс до',
    bpAfter: 'АД после',
    pulseAfter: 'Пульс после',
    illnessOnset: 'Начало заболевания',
    helpPlace: 'Место оказания',
    outcomeHelp: 'Результат помощи',
    outcomeVisit: 'Результат выезда',
    hospital: 'Стационар',
    refusalOnPaper: 'Отказ подписан на бумаге',
    refusalRequired: 'Отметьте, если отказ подписан на бумаге; подпись на экране — в приложении врача',
    transfer103: 'Номер бригады 103 и время',
    conditions: {
      satisfactory: 'Удовлетворительное',
      moderate: 'Средней тяжести',
      severe: 'Тяжёлое',
      very_severe: 'Крайне тяжёлое',
      clinical_death: 'Клиническая смерть',
    },
    natures: {
      acute_illness: 'Острое заболевание',
      chronic_illness: 'Хроническая болезнь',
      trauma: 'Травма',
      accident: 'Несчастный случай',
      childbirth: 'Роды',
      pregnancy_pathology: 'Патология беременности',
      other: 'Прочее',
    },
    onsets: { under_hour: 'До часа', under_day: 'До суток', over_day: 'Больше суток' },
    helpPlaces: { on_scene: 'На месте', in_vehicle: 'В машине' },
    outcomeHelps: {
      improvement: 'Улучшение',
      no_change: 'Без изменений',
      deterioration: 'Ухудшение',
      death: 'Смерть',
    },
    outcomeVisits: {
      helped_on_scene: 'Помощь оказана, оставлен на месте',
      hospitalized: 'Госпитализирован',
      refused_hospitalization: 'Отказ от госпитализации',
      refused_help: 'Отказ от помощи',
      transferred_103: 'Передан бригаде 103',
      no_patient: 'Пациент не найден',
      false_call: 'Ложный вызов',
      death_before_arrival: 'Смерть до прибытия',
      death_on_scene: 'Смерть в присутствии',
    },
  },
  payments: {
    method: 'Способ',
    amount: 'Сумма, сум',
    receiptNo: 'Номер чека',
    receiptPhoto: 'Фото чека',
    note: 'Комментарий',
    add: 'Принять оплату',
    added: 'Оплата принята',
    paid: 'Принято',
    owed: 'Осталось',
    deleteReason: 'Причина удаления',
    methods: {
      cash: 'Наличные',
      card_terminal: 'Терминал',
      payme: 'Payme',
      click: 'Click',
      transfer: 'Перевод',
      contract: 'По договору',
    },
    contractOnly: 'По договору можно оплатить только вызов от организации',
    // A cash or terminal payment taken without its number (settings receipt_required).
    noReceipt: 'без чека',
    optional: (label: string) => `${label} (необязательно)`,
  },
  shifts: {
    title: 'Смены',
    active: 'Бригады на линии',
    history: 'История смен',
    goOnLine: 'Выход на линию',
    end: 'Снять с линии',
    ended: 'Бригада снята с линии',
    started: 'Бригада на линии',
    vehicle: 'Машина',
    lead: 'Старший бригады',
    leadShort: 'Старший',
    driver: 'Водитель',
    second: 'Второй медработник',
    driverChecked: 'Водитель допущен (предрейсовый осмотр)',
    startedAt: 'На линии с',
    endedAt: 'Снята',
    totals: 'Итог смены',
    callsCount: 'Вызовов',
    revenue: 'Приход',
    expensesTitle: 'Расходы',
    cashCollected: 'Наличными',
    cashExpenses: 'Расходы из выручки',
    cashToHandover: 'К сдаче',
    reimbursable: 'К возмещению',
    noReceipt: 'Без чека (наличные и терминал)',
    confirm: 'Подтвердить итог',
    confirmedByDoctor: 'Подтверждено старшим бригады',
    confirmedByAdmin: 'Подтверждено админом',
    notConfirmed: 'Не подтверждено',
    addExpense: 'Внести расход',
    category: 'Категория',
    amount: 'Сумма, сум',
    source: 'Источник средств',
    liters: 'Литры',
    odometer: 'Одометр, км',
    station: 'АЗС',
    receipt: 'Фото чека',
    occurredAt: 'Дата и время',
    sources: {
      shift_cash: 'Из выручки смены',
      company_card: 'Карта компании',
      own: 'Свои деньги',
    },
    status: 'Статус',
    statuses: { pending: 'Не подтверждён', approved: 'Подтверждён', rejected: 'Отклонён' },
    approve: 'Подтвердить',
    reject: 'Отклонить',
    reviewNote: 'Комментарий',
    noCrews: 'Сейчас на линии никого нет',
    noExpenses: 'Расходов нет',
    errors: {
      receipt: 'Для этой категории нужно фото чека',
      liters: 'Для топлива нужны литры',
      odometer: 'Для топлива нужен одометр',
      category: 'Категория недоступна',
      vehicle: 'Машина недоступна',
      source: 'Источник средств не подходит',
      amount: 'Недопустимая сумма',
      staff: 'Сотрудник не подходит для этого места в бригаде',
      driverChecked: 'Без отметки о допуске водителя выход на линию невозможен',
    },
    closed: 'Закрытые смены',
    period: 'Период',
    onLineFor: 'На линии',
    confirmations: 'Подтверждения',
    noClosed: 'За выбранный период закрытых смен нет',
    expensesOfShift: 'Расходы смены',
  },
  patients: {
    title: 'Пациенты',
    searchPlaceholder: 'Телефон или ФИО (от 2 символов)',
    searchHint: 'Поиск по любому формату номера: +998 90 123-45-67, 901234567',
    add: 'Новый пациент',
    edit: 'Изменить данные',
    createTitle: 'Новый пациент',
    editTitle: 'Данные пациента',
    notFound: 'Пациенты не найдены',
    foundLimit: 'Показаны первые 10 совпадений — уточните запрос',
    cardData: 'Данные',
    cardPhones: 'Телефоны',
    cardNotes: 'Примечания',
    cardConsent: 'Согласие на обработку персональных данных',
    cardHistory: 'История вызовов',
    // Two cards for one person (TZ 6.4; STAGE_5).
    merge: 'Объединить с…',
    mergeTitle: (name: string) => `Объединить карточку с «${name}»`,
    mergeHint: 'Выберите вторую карточку. Её вызовы и платежи перейдут в эту, телефоны объединятся, а вторая карточка отключится и будет ссылаться на эту.',
    mergePick: 'Вторая карточка',
    mergeSearch: 'Телефон или ФИО, не меньше 2 символов',
    mergeDo: 'Объединить',
    merged: 'Карточки объединены',
    mergedInto: (id: number) => `Объединена с карточкой №${id}`,
    inactive: 'Отключена',
    historyEmpty: 'Вызовов пока нет. История появится вместе с вызовами (этап 2).',
    fullName: 'ФИО',
    birthDate: 'Дата рождения',
    age: 'Возраст',
    sex: 'Пол',
    sexMale: 'Мужской',
    sexFemale: 'Женский',
    sexUnknown: 'Не указан',
    phones: 'Телефоны',
    addPhone: 'Добавить телефон',
    removePhone: 'Удалить телефон',
    lang: 'Язык уведомлений',
    langRu: 'Русский',
    langUz: 'Узбекский',
    sms: 'SMS',
    noSms: 'Пациент просил не присылать SMS',
    notes: 'Примечания (хронические заболевания, аллергии)',
    consent: 'Согласие получено',
    consentAt: 'Дата и время согласия',
    consentNone: 'Не получено',
    birthInFuture: 'Дата рождения не может быть в будущем',
    notFoundPatient: 'Пациент не найден',
    years: (n: number) => {
      const mod10 = n % 10;
      const mod100 = n % 100;
      if (mod10 === 1 && mod100 !== 11) return `${n} год`;
      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} года`;
      return `${n} лет`;
    },
  },
  setup: {
    title: 'Мастер первого запуска',
    lead: 'Несколько шагов, чтобы система была готова к первому вызову. Любой шаг можно пропустить и вернуться к нему позже: мастер помнит, где вы остановились. Он всегда открыт в «Настройках».',
    open: 'Открыть мастер первого запуска',
    openHint: 'Организация, логотип, карта, прайс, сотрудники, машины и первый оператор — по шагам.',
    finishedNote: 'Мастер пройден. Шаги можно пройти ещё раз — ничего не сбросится.',
    next: 'Готово, дальше',
    skip: 'Пропустить',
    back: 'Назад',
    finish: 'Завершить',
    finished: 'Мастер пройден. Система готова к работе.',
    done: 'Готово',
    skipped: 'Пропущено',
    steps: {
      org: 'Организация',
      logo: 'Логотип',
      money: 'Время и деньги',
      map: 'Карта',
      prices: 'Прайс',
      staff: 'Сотрудники',
      vehicles: 'Машины',
      operator: 'Первый оператор',
    },
    hints: {
      org: 'Название и реквизиты печатаются в карте вызова, счёте и итогах смены; название подписывает сообщения пациентам.',
      logo: 'Логотип печатается в шапке документов. PNG или JPEG.',
      money: 'Часовой пояс и валюта заданы при установке. Итог вызова округляется вверх до шага ниже.',
      map: 'Граница города делит километры на городские и загородные. Геозона клиники: выезд из неё и возврат — события машины.',
      prices: 'Скачайте шаблон, заполните его и загрузите — система покажет, что добавится и что изменится, и только после вашего подтверждения запишет. Строки можно добавлять и вручную в таблице ниже.',
      staff: 'Врачи, фельдшеры, водители и диспетчеры: из Excel по шаблону или вручную. Телефон нужен для Telegram.',
      vehicles: 'Бортовой номер, госномер и модель. IMEI трекера можно внести позже — машина появится в Traccar сама.',
      operator: 'Учётная запись с ролью «Оператор» для диспетчера. Передайте ему логин и пароль; при первом входе он откроет смену.',
    },
    tz: 'Часовой пояс',
    tzHint: 'Меняется только в настройках сервера (TZ в /opt/sos/app/.env).',
    currency: 'Валюта',
    currencyValue: (code: string) => (code === 'UZS' ? 'сум (UZS), целые суммы' : code),
    count: (count: number) => `Сейчас в справочнике: ${count}`,
    operators: (count: number) => `Операторов: ${count}`,
    orgNameRequired: 'Как организацию называют пациенты',
    import: {
      template: 'Скачать шаблон',
      upload: 'Загрузить заполненный файл',
      replace: 'Загрузить другой файл',
      checking: 'Проверяем файл...',
      apply: 'Применить',
      applied: (added: number, changed: number) => `Записано: добавлено ${added}, изменено ${changed}`,
      nothing: 'В файле нет ничего нового',
      hasErrors: 'Исправьте строки с ошибками в файле и загрузите его снова: пока есть ошибки, ничего не записывается.',
      line: 'Строка',
      status: 'Что будет',
      statuses: {
        new: 'Добавится',
        update: 'Изменится',
        same: 'Без изменений',
        error: 'Ошибка',
      } as Record<string, string>,
      counts: (added: number, changed: number, same: number, errors: number) =>
        `Добавится ${added}, изменится ${changed}, без изменений ${same}, с ошибками ${errors}`,
      failed: 'Не удалось прочитать файл',
    },
  },
  phone: {
    // The line's calls (STAGE_T T.3).
    ringing: 'Звонок на линию',
    talking: (duration: string) => `Идёт разговор ${duration}`,
    ended: 'Разговор окончен',
    unknownCaller: 'Номер не найден в картотеке',
    age: (years: number) => `${String(years)} лет`,
    callsToday: (count: number) => `Звонил ${String(count)} раз за сутки`,
    lastCalls: 'Последние вызовы',
    recentRow: (number: string, at: string, reason: string, address: string, status: string) =>
      `${number} · ${at} · ${reason} · ${address} · ${status}`,
    joinParts: (parts: readonly string[]) => parts.join(' · '),
    noCalls: 'Вызовов с этого номера не было',
    take: 'Принять вызов',
    createCall: 'Создать вызов',
    callTaken: (number: string) => `Вызов ${number} создан`,
    notACall: 'Это не вызов',
    resolutions: {
      info: 'Справка',
      mistake: 'Ошибка',
      called_back_mobile: 'Перезвонили с мобильного',
    } as Record<string, string>,
    missed: (count: number) => `Пропущено: ${String(count)}`,
    missedTitle: 'Пропущенные звонки',
    missedNone: 'Пропущенных нет',
    missedRow: (count: number) => (count > 1 ? `${String(count)} звонка` : 'звонок'),
    callBack: 'Перезвонить',
    markCalledBack: 'Отметить: перезвонили с мобильного',
    call: 'Позвонить',
    calling: 'Звоним: снимите трубку телефона линии',
    callOff: 'Телефония не подключена или выключена в настройках',
    callNeedsShift: 'Звонить можно из смены за пультом',
    callFailed: 'АТС не отвечает: позвоните с телефона линии',
    listen: 'Прослушать',
    noRecording: 'Записи нет',
    recordingGone: 'Запись удалена по сроку хранения',
    recordingNeedsShift: 'Записи слушают из смены за пультом',
    recordingFailed: 'Не удалось загрузить запись',
    calls: 'Звонки',
    noPhoneCalls: 'Звонков по этому вызову нет',
    directions: { in: 'Входящий', out: 'Исходящий', click: 'Из системы' } as Record<string, string>,
    statuses: {
      ringing: 'Звонит',
      answered: 'Принят',
      missed: 'Пропущен',
      busy: 'Занято',
      failed: 'Не прошёл',
    } as Record<string, string>,
    journal: {
      tab: 'Звонки',
      period: 'Период',
      direction: 'Направление',
      status: 'Итог',
      missedOnly: 'Только пропущенные',
      number: 'Номер',
      export: 'Excel',
      columns: {
        started_at: 'Время',
        direction: 'Направление',
        number: 'Номер',
        patient: 'Пациент',
        status: 'Итог',
        answer: 'Ответ через',
        duration: 'Разговор',
        call: 'Вызов',
        operator: 'Оператор',
        resolution: 'Отметка',
        recording: 'Запись',
      },
    },
  },
  settings: {
    title: 'Настройки',
    defaultValue: 'Значение по умолчанию',
    groups: {
      org: 'Организация',
      pricing: 'Расчёт стоимости',
      dispatch: 'Диспетчерская',
      reports: 'Отчёты',
      city: 'Граница города',
      base: 'Геозона клиники',
      templates: 'Шаблоны уведомлений',
      notifications: 'Уведомления',
      vendor: 'Поставщик (техподдержка)',
      product: 'Продукт',
    },
    keys: {
      org: 'Организация',
      product: 'Продукт',
      night_interval: 'Ночной интервал',
      position_stale_seconds: 'GPS-позиция устаревает через, с',
      queue_alert_minutes: 'Тревога: вызов ждёт назначения дольше, мин',
      desk_unattended_minutes: 'Заведующему: тревога без ответа диспетчера дольше, мин',
      intake_target_seconds: 'Норма приёма вызова, с',
      default_promised_minutes: 'Обещанное время по умолчанию, мин',
      avg_call_minutes_window_days: 'Среднее время на вызове — за последние, дней',
      alert_sound: 'Звук тревог в диспетчерской',
      rounding_uzs: 'Округление итога до, сум',
      base_services: 'Базовая услуга по типу вызова',
      receipt_required: 'Номер фискального чека обязателен',
      fuel_deviation_pct: 'Топливо: отклонение км по одометру от трека больше, %',
      city_polygon: 'Полигон города (GeoJSON)',
      base_polygon: 'Полигон клиники (GeoJSON)',
      estimated_distance_coef: 'Коэффициент к прямому расстоянию',
      notification_templates: 'Шаблоны',
      vendor_access: 'Доступ поставщика (техподдержки) разрешён',
      vendor_monitoring: 'Мониторинг поставщиком: раз в час отправлять версию, состояние сервисов и место на диске',
      telephony_on: 'Телефония на пульте: карточка звонка, пропущенные, «Позвонить»',
      telephony_announce_lang: 'Объявления звонящему — на языке',
      recordings_retention_days: 'Записи разговоров хранятся, дней',
    },
    // The telephony's screen (STAGE_T T.4).
    telephony: {
      title: 'Телефония',
      notHere:
        'На этом сервере АТС не запущена: нужны профиль telephony и пароль ARI в .env (см. docs/telephony.md). Номер линии задаётся там же, в .env, а не здесь.',
      switchHint:
        'Выключенная, телефония не показывается на пульте; звонки по-прежнему идут через АТС, записываются и попадают в журнал. После включения пульт показывает звонки с этого момента.',
      status: 'Состояние',
      refresh: 'Обновить',
      pbx: 'АТС (Asterisk)',
      pbxUp: 'на связи',
      pbxDown: 'нет связи',
      checkedAt: (at: string) => `проверено ${at}`,
      cme: 'Cisco CME (OPTIONS)',
      endpoint: { online: 'доступна', offline: 'недоступна', unknown: 'неизвестно' } as Record<string, string>,
      lastCall: 'Последний звонок',
      noCalls: 'Звонков ещё не было',
      lastCallRow: (at: string, direction: string, number: string, status: string) =>
        `${at} · ${direction} · ${number} · ${status}`,
      prompts: 'Объявления',
      promptsHint:
        'Файл WAV или MP3 до 10 МБ; сервер переводит его в 8 кГц моно A-law и обрезает до 60 с. Действует со следующего звонка. Без файла звонящий ничего не слышит.',
      kinds: {
        announce: 'Разговор записывается',
        callback: 'Мы вам перезвоним (после пропущенного)',
      } as Record<string, string>,
      langs: { ru: 'Русский', uz: 'Узбекский' } as Record<string, string>,
      languages: { ru: 'русском', uz: 'узбекском', 'ru+uz': 'русском, затем узбекском' } as Record<string, string>,
      absent: 'нет файла',
      present: (seconds: string, at: string) => `${seconds} с, загружен ${at}`,
      upload: 'Загрузить',
      replace: 'Заменить',
      uploaded: 'Объявление загружено',
      uploadFailed: 'Не удалось загрузить',
      refused: (why: string) => `Файл не принят: ${why}`,
      remove: 'Удалить',
      removeConfirm: 'Удалить объявление? Звонящий перестанет его слышать.',
      removed: 'Объявление удалено',
      listenFailed: 'Не удалось загрузить объявление',
    },
    notifications: {
      channels: 'Каналы',
      telegram: 'Telegram сотрудникам',
      sms: 'SMS пациенту',
      push: 'Push на планшет',
      events: 'События',
      eventsHint: 'Снимите галочку, чтобы это событие не отправлялось ни по одному каналу.',
      audiences: {
        patient: 'Пациенту',
        doctor: 'Медработнику',
        operator: 'Оператору',
        admin: 'Заведующему',
      } as Record<string, string>,
      eventNames: {
        'patient.assigned': 'Назначена бригада',
        'patient.departed': 'Бригада выехала',
        'patient.delayed': 'Задержка в пути',
        'patient.completed_rating': 'Просьба оценить выезд',
        'doctor.assigned': 'Новый вызов',
        'doctor.reassigned': 'Вызов передан бригаде',
        'doctor.operator_message': 'Сообщение оператора',
        'operator.queue_overdue': 'Вызов просрочен',
        'operator.incident': 'Инцидент на вызове',
        'operator.tracker_offline': 'Трекер не отвечает',
        'admin.incident': 'Инцидент',
        'admin.complaint': 'Жалоба',
        'admin.shift_totals': 'Итог смены',
        'admin.insurance_due': 'Страховка или ТО',
        'admin.desk_unattended': 'Диспетчерская без ответа',
        'admin.service_down': 'Сбой в системе',
        'admin.maps_updated': 'Карты обновлены',
        'admin.maps_update_failed': 'Обновление карт не удалось',
      } as Record<string, string>,
      bot: 'Telegram-бот',
      botToken: 'Токен бота',
      secretKept: '•••• сохранён',
      botTokenHint: 'Получите у @BotFather. Сохранённый токен показывается маской; оставьте поле пустым, чтобы не менять его.',
      botTokenFromEnv: 'Токен задан в окружении (TELEGRAM_BOT_TOKEN) и имеет приоритет над сохранённым',
      botCheck: 'Проверить и установить webhook',
      botConnected: (name: string) => `Бот @${name} подключён, webhook установлен`,
      botNoWebhook: (name: string) => `Бот @${name} отвечает, но webhook не установлен`,
      botNotConfigured: 'Токен не задан',
      botError: (text: string) => `Telegram отказал: ${text}`,
      botDone: 'Webhook установлен',
      links: 'Привязанные сотрудники',
      linksEmpty: 'Пока никто не привязан: сотрудник пишет боту и делится номером телефона',
      linkedAt: 'Привязан',
      toStaff: 'Справочник сотрудников',
      smsTitle: 'SMS пациенту',
      smsProvider: 'Провайдер',
      smsProviders: {
        log: 'Отключено (только журнал)',
        eskiz: 'Eskiz.uz',
        playmobile: 'Play Mobile',
      } as Record<string, string>,
      smsLogin: 'Логин',
      smsPassword: 'Пароль',
      smsSender: 'Имя отправителя',
      smsCheck: 'Проверить',
      smsTest: 'Отправить тестовое SMS',
      smsTestPhone: 'Номер для теста',
      smsChecked: (account: string) => `Провайдер отвечает: ${account}`,
      smsTestSent: (id: string) => `Отправлено, идентификатор ${id}`,
      smsTestLogged: 'Провайдер отключён: сообщение только записано в журнал',
      smsRefused: (text: string) => `Провайдер отказал: ${text}`,
      smsHint: 'Пароль хранится скрытым: показывается маской, в журнале изменений — только факт правки. Значения из окружения (SMS_*) имеют приоритет.',
    },
    orgName: 'Название',
    orgPhones: 'Телефоны',
    orgRequisites: 'Реквизиты',
    orgLogo: 'Логотип для печатных форм',
    logoUpload: 'Загрузить логотип',
    logoReplace: 'Заменить',
    logoRemove: 'Убрать',
    logoFailed: 'Не удалось загрузить логотип (PNG или JPEG)',
    // The product (STAGE_7 7.7): the vendor's.
    productName: 'Название продукта',
    productShortName: 'Короткое название (под иконкой на планшете)',
    productColor: 'Основной цвет',
    productIcon: 'Иконка приложения',
    productIconUpload: 'Загрузить иконку',
    productIconHint: 'Квадратный PNG, не меньше 192×192 — с ней планшет устанавливает приложение бригады',
    productIconFailed: 'Не удалось загрузить иконку (квадратный PNG от 192×192)',
    vendorName: 'Поставщик',
    vendorPhone: 'Телефон поставщика',
    vendorEmail: 'Email поставщика',
    vendorSite: 'Сайт поставщика',
    about: 'О программе',
    aboutProduct: 'Продукт',
    aboutVersion: 'Версия',
    aboutShortName: 'Короткое название',
    aboutColor: 'Основной цвет',
    aboutIcon: 'Иконка',
    aboutIconDefault: 'стандартная',
    aboutReadOnly: 'Эти данные задаёт поставщик; по вопросам работы системы — контакты выше',
    addPhone: 'Добавить телефон',
    nightFrom: 'С',
    nightTo: 'До',
    cityHint:
      'Кликайте по карте, чтобы поставить вершины; двойной клик замыкает контур. Вершины можно перетаскивать.',
    baseHint:
      'Контур клиники или гаража: выезд за него и возврат записываются событиями машины. Рисуется так же, как граница города.',
    nightRuleHint: 'Ночная наценка задаётся правилом',
    baseServicesHint:
      'Услуга из прайса, с которой начинается вызов этого типа: ставится при создании и при смене типа, пока услуги не правили вручную. Не задана — вызов считается по тарифу.',
    baseServiceNone: 'Не задана — по тарифу',
    receiptRequiredHint:
      'Включено — платёж наличными или терминалом не принимается без номера чека (не короче 4 символов). Выключено — номер можно не вводить; такие платежи отмечаются «без чека» в «Кассе за день», итогах смены и в платежах вызова.',
    toNightRule: 'Перейти к правилу',
    cityDeveloper: 'Для разработчика',
    cityJsonHint:
      'Polygon, MultiPolygon или FeatureCollection в координатах [долгота, широта]. Пустая коллекция — граница не задана.',
    cityClear: 'Очистить',
    cityNotSet: 'Граница не задана',
    cityDrawing: 'Рисование',
    cityPoints: (count: number) => `Вершин: ${count}`,
    cityComplex:
      'Фигура сложнее одного контура: её можно посмотреть на карте и изменить в блоке «Для разработчика», либо нажать «Очистить» и нарисовать заново.',
    templatesHint: 'Имя шаблона — кому и о чём, латиницей через точку: doctor.assigned, operator.queue_overdue, patient.crew_assigned. Кому: patient, doctor, operator или admin — у сотрудника с двумя ролями уведомления каждой роли свои. Тексты появятся на этапе 6.',
    templateName: 'Имя',
    templateText: 'Текст',
    monitoringHint:
      'Персональные данные не отправляются: ни имён, ни телефонов, ни адресов, ни вызовов. Установка называется своим идентификатором. Ниже — ровно то, что уйдёт в отчёте.',
    monitoringPreview: 'Что отправляется',
    monitoringNotConfigured: 'Адрес мониторинга не задан в этом выпуске: даже с согласием ничего не отправится.',
    addTemplate: 'Добавить шаблон',
    langRu: 'Русский',
    langUz: 'Узбекский',
  },
} as const;
